import React, { useState, useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, Area, AreaChart,ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine } from 'recharts';
import { 
  Activity, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Camera,
  Copy,
  Eye, 
  Filter, 
  Search, 
  ChevronDown, 
  ChevronRight,
  Home,
  GitBranch,
  Play,
  TestTube,
  TestTubes,
  Calendar,
  Loader2,
  TrendingUp,
  AlertTriangle,
  ExternalLink,
  Send
} from 'lucide-react';
import azureDevOpsService from '../src/services/azureDevOpsService';
import TestResultsTable from './components/TestResultsTable';
import relaunchTracker from './services/relaunchTracker';
import { toast, Toaster } from 'react-hot-toast';
import { sendDashboardToSlack } from './services/slackService';
import html2canvas from 'html2canvas';
import elasticAuthService from './services/elasticAuthService';
import { useZoneDeTrace } from './services/traceZone';
import ElasticAuthModal from './ElasticAuthModal';

// Router integration: use react-router for real URLs
import { Routes as RRRoutes, Route as RRRoute, useNavigate as rrUseNavigate, useLocation as rrUseLocation, Navigate as RRNavigate } from 'react-router-dom';
import AiAnalysisDrawer from './components/AiAnalysisDrawer';
import TestCaseSearchBar from './components/TestCaseSearchBar';
import rootCauseService from '../src/services/rootCauseService';
import bugLinkService from '../src/services/bugLinkService';
import BatchRootCauseBar from './components/BatchRootCauseBar';
import { computeTestStats, computeSuccessRate, statusLabel, statusChartColor, matchesStatusFilter } from './utils/testStatus';
import { STATUS_THRESHOLDS } from './config/statusConfig';

const useRouter = () => {
  const rrNavigate = rrUseNavigate();
  const location = rrUseLocation();
  const [params, setParams] = useState({});
  const debugMode = useMemo(() => new URLSearchParams(location.search).get('debug') === '1', [location.search]);

  const robustDecode = (value) => {
    if (!value) return value;
    let attempt = value;
    for (let i = 0; i < 3; i++) { // up to triple-encoding safety
      try {
        const decoded = decodeURIComponent(attempt);
        if (decoded === attempt) break; // no further change
        attempt = decoded;
      } catch {
        break; // stop if invalid sequence
      }
    }
    return attempt;
  };

  const parsePath = (pathname, search) => {
    const pathParts = pathname.split('/').filter(Boolean);
    const newParams = {};
    if (search) {
      const sp = new URLSearchParams(search);
      for (const [k, v] of sp.entries()) newParams[k] = v;
    }

    if (pathParts[0] === 'pipeline' && pathParts[1]) {
      newParams.pipelineId = pathParts[1];
      if (pathParts[2] === 'build' && pathParts[3]) {
        newParams.buildId = pathParts[3];
        if (pathParts[4] === 'run' && pathParts[5]) {
          newParams.runId = pathParts[5];
          if (pathParts[6] === 'testsuite' && pathParts[7]) {
            // Handle potentially encoded testsuite name - join remaining parts in case name has slashes
            const suiteNamePart = pathParts.slice(7).join('/').split('?')[0]; // everything after testsuite, before query
            newParams.testSuiteName = robustDecode(suiteNamePart);
          }
        }
      }
    } else if (pathParts[0] === 'testcase' && pathParts[1]) {
      // Join all parts between testcase and /history in case test name contains encoded slashes
      let testcaseNameParts = [];
      let historyIndex = -1;
      for (let i = 1; i < pathParts.length; i++) {
        if (pathParts[i] === 'history') {
          historyIndex = i;
          break;
        }
        testcaseNameParts.push(pathParts[i]);
      }
      newParams.testcaseName = robustDecode(testcaseNameParts.join('/'));
      if (historyIndex !== -1 && pathParts[historyIndex + 1]) {
        newParams.pipelineId = pathParts[historyIndex + 1];
      }
    }
    return newParams;
  };

  useEffect(() => {
    const next = parsePath(location.pathname.replace(/^\/readyapi/, '') || '/', location.search);
    setParams(next);
  }, [location.pathname, location.search]);

  const navigate = (path) => {
    if (path && path.startsWith('/tests')) {
      rrNavigate(path);
      return;
    }
    const encoded = path.replace(/\/testsuite\/([^?]+)/, (m, p1) => (p1.includes('%') ? `/testsuite/${p1}` : `/testsuite/${encodeURIComponent(p1)}`));
    const baseTarget = `/readyapi${encoded.startsWith('/') ? '' : '/'}${encoded}`;

    let finalTarget = baseTarget;
    try {
      const [pathname, search = ''] = baseTarget.split('?');
      const sp = new URLSearchParams(search);
      const currentParams = new URLSearchParams(location.search);
      const returnTo = currentParams.get('returnTo') || getStoredReturnTo();
      if (returnTo && !sp.has('returnTo')) {
        sp.set('returnTo', returnTo);
      }
      const configMode = currentParams.get('config');
      if (configMode && !sp.has('config')) {
        sp.set('config', configMode);
      }
      const nextSearch = sp.toString();
      finalTarget = nextSearch ? `${pathname}?${nextSearch}` : pathname;
    } catch {}

    rrNavigate(finalTarget);
  };

  // Replace only the query string without triggering full navigation (prevents flicker)
  const replaceQuery = (patch) => {
    const pathname = location.pathname; // already includes /readyapi prefix
    const sp = new URLSearchParams(location.search);
    Object.entries(patch || {}).forEach(([k, v]) => {
      if (v === undefined || v === null) sp.delete(k); else sp.set(k, v);
    });
    const newSearch = `?${sp.toString()}`;
    const newUrl = pathname + newSearch;
    if (newUrl !== (pathname + location.search)) {
      window.history.replaceState(null, '', newUrl);
      // Manually update params after query change
      setParams(parsePath(pathname.replace(/^\/readyapi/, '') || '/', newSearch));
    }
  };

  const currentPath = (location.pathname.replace(/^\/readyapi/, '') || '/') + (location.search || '');
  return { currentPath, params, navigate, replaceQuery, debugMode };
};

// Components
const StatusBadge = ({ status }) => {
  const styles = {
    succeeded: 'bg-green-100 text-green-800 border-green-300',
    failed: 'bg-red-100 text-red-800 border-red-300',
    running: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    partiallysucceeded: 'bg-orange-100 text-orange-800 border-orange-300',
    passed: 'bg-green-100 text-green-800 border-green-300'
  };

  const icons = {
    success: <CheckCircle className="w-4 h-4" />,
    failed: <XCircle className="w-4 h-4" />,
    running: <AlertTriangle className="w-4 h-4" />
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.failed}`}>
      {icons[status]}
      {({'succeeded':'Réussi','passed':'Réussi','failed':'Échoué','partiallysucceeded':'Partiellement réussi','running':'En cours','canceled':'Annulé'})[status?.toLowerCase()] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : '')}
    </span>
  );
};

const formatDuration = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = (seconds % 60).toFixed(0);
  return `${minutes}m ${remainingSeconds}s`;
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('fr-FR');
};

const getStoredReturnTo = () => {
  try {
    return localStorage.getItem('readyapiReturnTo');
  } catch {
    return null;
  }
};

const getEnvStatusFromPercentage = (pct,nbcritical) => {
  const n = Number(pct) || 0;
  const ncritical = Number(nbcritical)
  if (ncritical > 0) 
  { if (ncritical ==1)
    return { label: 'pas stable - ' + ncritical+' bug critique', color: 'red' };
    else return { label: 'pas stable - ' + ncritical+' bugs critiques', color: 'red' };
    }

  if (n >= STATUS_THRESHOLDS.STABLE) return { label: 'stable', color: 'green' };
  if (n >= STATUS_THRESHOLDS.WARNING) return { label: 'warning', color: 'yellow' };
  return { label: 'pas stable', color: 'red' };
};

// Navigation Component
const Navigation = ({ currentPath, navigate, version = 'develop' }) => {
  const pathParts = currentPath.split('/').filter(Boolean);
  const storedReturnTo = getStoredReturnTo();
  const dashboardPath = storedReturnTo || '/';
  
  const breadcrumbs = [
    { label: 'Dashboard', path: dashboardPath, icon: <Home className="w-4 h-4" /> }
  ];

  if (pathParts[0] === 'pipeline' && pathParts[1]) {
    breadcrumbs.push({
      label: pathParts[1],
      path: `/pipeline/${pathParts[1]}?version=${version}`,
      icon: <GitBranch className="w-4 h-4" />
    });

    if (pathParts[2] === 'build' && pathParts[3]) {
      breadcrumbs.push({
        label: `${pathParts[3]}`,
        // Ajout du flag nav=build pour éviter restauration automatique quand l'utilisateur revient à la liste des suites
        path: `/pipeline/${pathParts[1]}/build/${pathParts[3]}?version=${version}&nav=build`,
        icon: <Play className="w-4 h-4" />
      });

      if (pathParts[4] === 'run' && pathParts[5]) {
        breadcrumbs.push({
          label: `${pathParts[5]}`,
          path: `/pipeline/${pathParts[1]}/build/${pathParts[3]}/run/${pathParts[5]}?version=${version}`,
          icon: <TestTubes className="w-4 h-4" />
        });

        if (pathParts[6] === 'testsuite' && pathParts[7]) {
        const decodedName = pathParts[7] ? pathParts[7].replace(/%20/g,' ') : pathParts[7];
        breadcrumbs.push({
          label: decodedName,
          path: `/pipeline/${pathParts[1]}/build/${pathParts[3]}/run/${pathParts[5]}/testsuite/${pathParts[7]}?version=${version}`,
          icon: <TestTube  className="w-4 h-4" />
        });
      }
      }
    }
  } else if (pathParts[0] === 'testcase' && pathParts[1]) {
    breadcrumbs.push({
      label: `${pathParts[1]}`,
      path: currentPath,
      icon: <TestTube className="w-4 h-4" />
    });
  }

  return (
    <nav className="bg-white shadow-sm border-b mb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 py-3">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
              <button
                onClick={() => navigate(crumb.path)}
                className="flex items-center space-x-1 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors"
              >
                {crumb.icon}
                <span>{crumb.label}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
    </nav>
  );
};
// Pipeline configurations by version
const PIPELINE_CONFIGS = {
  develop: {
    pipelinesList: [
      { id: 304, name: "DP Tests" },
      { id: 305, name: "DE Tests" },
      { id: 303, name: "Admin Tests" },
      { id: 355, name: "Investigation Tests" }
    ],
    pipelineIds: [304, 305, 303, 355]
  },
  "25.1": {
    pipelinesList: [
      { id: 389, name: "DP Tests" },
      { id: 392, name: "DE Tests" },
      { id: 390, name: "Admin Tests" },
      { id: 391, name: "Investigation Tests" }
    ],
    pipelineIds: [389, 392, 390, 391]
  },
  "25.2": {
    pipelinesList: [
      { id: 304, name: "DP Tests" },
      { id: 305, name: "DE Tests" },
      { id: 303, name: "Admin Tests" },
      { id: 355, name: "Investigation Tests" }
    ],
    pipelineIds: [304, 305, 303, 355]
  }
};

// Alternate pipeline configs for the new dashboard
const PIPELINE_CONFIGS_NEW = {
  develop: {
    pipelinesList: [
      { id: 497, name: "DP Tests" },
      { id: 499, name: "DE Tests" },
      { id: 496, name: "Admin Tests" },
      { id: 501, name: "Investigation Tests" }
    ],
    pipelineIds: [497, 499, 496, 501]
  },
  "25.1": {
    pipelinesList: [
      { id: 389, name: "DP Tests" },
      { id: 392, name: "DE Tests" },
      { id: 390, name: "Admin Tests" },
      { id: 391, name: "Investigation Tests" }
    ],
    pipelineIds: [389, 392, 390, 391]
  },
  "25.2": {
    pipelinesList: [
      { id: 518, name: "DP Tests" },
      { id: 519, name: "DE Tests" },
      { id: 517, name: "Admin Tests" },
      { id: 520, name: "Investigation Tests" }
    ],
    pipelineIds: [518, 519, 517, 520]
  }
};

const getPipelineConfigs = (configMode) => (
  configMode === 'new' ? PIPELINE_CONFIGS_NEW : PIPELINE_CONFIGS
);

// Tous les pipeline IDs ReadyAPI (toutes versions) pour la recherche globale
const ALL_READYAPI_PIPELINE_IDS = [...new Set(
  [...Object.values(PIPELINE_CONFIGS), ...Object.values(PIPELINE_CONFIGS_NEW)].flatMap(c => c.pipelineIds)
)];

// Mapping pipelineId → version (pour construire l'URL de navigation)
const READYAPI_PIPELINE_TO_VERSION = {};
for (const [version, config] of Object.entries(PIPELINE_CONFIGS_NEW)) {
  for (const id of config.pipelineIds) {
    READYAPI_PIPELINE_TO_VERSION[id] = version;
  }
}
for (const [version, config] of Object.entries(PIPELINE_CONFIGS)) {
  for (const id of config.pipelineIds) {
    if (!READYAPI_PIPELINE_TO_VERSION[id]) READYAPI_PIPELINE_TO_VERSION[id] = version;
  }
}

// Dashboard Component
const Dashboard = ({ navigate, configMode = 'default' }) => {
  const [pipelines, setPipelines] = useState([]);
  // Restaurer la version depuis localStorage ou utiliser 'develop' par défaut
  const [selectedVersion, setSelectedVersion] = useState(() => {
    try {
      return localStorage.getItem('selectedVersion') || 'develop';
    } catch (e) {
      return 'develop';
    }
  });
  const [isVersionLoading, setIsVersionLoading] = useState(false);
  const [globalStats, setGlobalStats] = useState({});
  const [trendData, setTrendData] = useState({});
  const [trendDays, setTrendDays] = useState(7);
  const [trendLoading, setTrendLoading] = useState(false);
  const [bugs, setBugs] = useState([]);
  const [explorationBugs, setExplorationBugs] = useState([]);
  const [allBugs, setAllBugs] = useState([]);
  const [groupAverages, setGroupAverages] = useState({});
  const [overallGroupAverage, setOverallGroupAverage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBugsModal, setShowBugsModal] = useState(false);
  const [isSendingToSlack, setIsSendingToSlack] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(new Set());

  const handleTriggerPipeline = async (pipelineId) => {
    setTriggerLoading(prev => new Set([...prev, pipelineId]));
    try {
      await azureDevOpsService.triggerPipeline(pipelineId);
    } catch (err) {
      console.error('Failed to trigger pipeline:', err);
    } finally {
      setTriggerLoading(prev => { const s = new Set(prev); s.delete(pipelineId); return s; });
    }
  };
  const pipelineConfigs = getPipelineConfigs(configMode);

  // Synchroniser avec le paramètre de version dans l'URL au chargement
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const versionFromUrl = urlParams.get('version');
    if (versionFromUrl && pipelineConfigs[versionFromUrl]) {
      setSelectedVersion(versionFromUrl);
      try {
        localStorage.setItem('selectedVersion', versionFromUrl);
      } catch (e) {
        console.warn('Impossible de sauvegarder la version dans localStorage:', e);
      }
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try{
      setIsVersionLoading(true);
      const config = pipelineConfigs[selectedVersion];
      const pipelinesData = await azureDevOpsService.getBuildsData(config.pipelinesList);
      setPipelines(pipelinesData);
      // Calculer les moyennes par groupe (successRate) et la moyenne globale
      try {
        const perGroup = {};
        for (const target of config.pipelinesList) {
          const found = pipelinesData.find(p => String(p.id) === String(target.id));
          const rate = found ? (typeof found.successRate === 'string' ? parseFloat(found.successRate) : Number(found.successRate)) : 0;
          perGroup[target.name] = isNaN(rate) ? 0 : rate;
        }
        const values = Object.values(perGroup);
        const sum = values.reduce((s, v) => s + (v || 0), 0);
        const overall = values.length ? sum / values.length : 0;
        setGroupAverages(perGroup);
        setOverallGroupAverage(overall);
      } catch (e) {
        console.warn('Erreur calcul moyennes de groupe:', e);
        setGroupAverages({});
        setOverallGroupAverage(0);
      }
      setLoading(true);
      setError(null);

      // KPIs globaux
      let totalPassed = 0, totalFailed = 0, totalSkipped = 0, totalDuration = 0, totalTests = 0;

      for (const p of pipelinesData) {
        totalPassed += p.passedTests;
        totalFailed += p.failedTests;
        totalSkipped += p.skippedTests || 0;
        totalDuration += p.totalDuration;
        totalTests += p.totalTests;
      }

      setGlobalStats({
        totalPassed,
        totalFailed,
        totalSkipped,
        averageSuccessRate: computeSuccessRate(totalPassed, totalTests, totalSkipped),
        averageDuration: pipelinesData.length ? totalDuration / pipelinesData.length : 0
      });
    } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Impossible de charger les données des pipelines');
      } finally {
        setLoading(false);
        setIsVersionLoading(false);
      }
    }
    fetchData();
  }, [selectedVersion]);

  useEffect(() => {
    const fetchTrendData = async () => {
      setTrendLoading(true);
      try {
        const config = pipelineConfigs[selectedVersion];
        const results = {};
        await Promise.all(config.pipelinesList.map(async (pipeline) => {
          try {
            results[pipeline.id] = await azureDevOpsService.getTrendDataForPipeline(pipeline.id, trendDays);
          } catch {
            results[pipeline.id] = [];
          }
        }));
        setTrendData(results);
      } catch (error) {
        console.error("Erreur chargement tendance tests:", error);
      } finally {
        setTrendLoading(false);
      }
    };
    fetchTrendData();
  }, [selectedVersion, trendDays]);
  // Récupérer Tous les bugs pour toutes les squads
  useEffect(() => {
    let mounted = true;
    const fetchAllBugs = async () => {
      try {
        const data = await azureDevOpsService.getBugsForAllSquads();
        if (mounted) setAllBugs(data);
      } catch (err) {
        console.error('Erreur récupération bugs :', err);
      }
    };

    fetchAllBugs();
    return () => { mounted = false; };
  }, []);
     
  // Récupérer les bugs pour la Squad Data Preparation
  useEffect(() => {
    let mounted = true;
    const fetchBugs = async () => {
      try {
        
        const data = await azureDevOpsService.getBugsForSquadDataPreparation();
        if (mounted) setBugs(data);
      } catch (err) {
        console.error('Erreur récupération bugs :', err);
      }
    };

    fetchBugs();
    return () => { mounted = false; };
  }, []);

  // Récupérer les bugs pour la Squad Data Exploration
  useEffect(() => {
    let mounted = true;
    const fetchExplorationBugs = async () => {
      try {
        const data = await azureDevOpsService.getBugsForSquadDataExploration();
        if (mounted) setExplorationBugs(data);
      } catch (err) {
        console.error('Erreur récupération bugs exploration :', err);
      }
    };

    fetchExplorationBugs();
    return () => { mounted = false; };
  }, []);

  // Fonction pour envoyer le dashboard vers Slack
  const handleSendToSlack = async () => {
    try {
      setIsSendingToSlack(true);
      toast.loading('Capture du dashboard en cours...', { id: 'slack' });

      // 1. Capturer TOUTE la page (y compris la partie scrollée)
      const body = document.body;
      const html = document.documentElement;
      
      // Sauvegarder la position actuelle du scroll
      const originalScrollPos = window.pageYOffset;
      
      // Calculer la hauteur totale de la page
      const fullHeight = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
      );
      
      // Capturer toute la page
      const canvas = await html2canvas(body, {
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        height: fullHeight,
        windowHeight: fullHeight,
        width: html.scrollWidth,
        scale: 0.8, // Réduire la résolution pour optimiser la taille
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      // Restaurer la position du scroll
      window.scrollTo(0, originalScrollPos);
      
      toast.loading('Préparation de l\'envoi...', { id: 'slack' });
      
      // Convertir en blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 0.9));

      // 2. Préparer les données du dashboard
      const bugsCriticalCount = selectedVersion === '25.1' ? 0 : allBugs.filter(p => String(p.severity) === String("1 - Critical")).length;
      
      const dashboardData = {
        version: selectedVersion,
        status: getEnvStatusFromPercentage(
          globalStats.averageSuccessRate,
          bugsCriticalCount
        ),
        overallAverage: Number(globalStats.averageSuccessRate).toFixed(1),
        bugsCritical: bugsCriticalCount,
        bugsTotal: allBugs.length,
        pipelines: pipelines.map(p => ({
          name: p.name,
          passedTests: p.passedTests || 0,
          failedTests: p.failedTests || 0,
          skippedTests: p.skippedTests || 0,
          totalTests: p.totalTests || 0,
          successRate: p.successRate || 0,
          duration: formatDuration(p.totalDuration)
        })),
        bugsLink: 'https://dev.azure.com/chapsvision/Fabrique/_queries/query/67020172-e295-4bb7-981f-87758ab02f7d/'
      };

      // 3. Envoyer vers Slack
      const result = await sendDashboardToSlack(dashboardData, blob);
      
      toast.success(`Dashboard envoyé vers Slack (${result.channel}) avec succès! 🎉`, { id: 'slack', duration: 4000 });
    } catch (err) {
      console.error('Erreur envoi Slack:', err);
      toast.error(`Erreur: ${err.message}`, { id: 'slack' });
    } finally {
      setIsSendingToSlack(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement des données...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Screenshot / Copy buttons (top-right fixed for Dashboard) */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
        <button
          onClick={async () => {
            try {
              toast.loading('Capture en cours...', { id: 'screenshot-db' });
              const el = document.documentElement; // capture toute la page
              const canvas = await html2canvas(el, { scale: 1 });
              const dataUrl = canvas.toDataURL('image/png');
              // Download
              const link = document.createElement('a');
              link.href = dataUrl;
              link.download = `screenshot-${Date.now()}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              toast.success('Screenshot téléchargé', { id: 'screenshot-db' });
            } catch (err) {
              console.error(err);
              toast.error('Erreur capture', { id: 'screenshot-db' });
            }
          }}
          className="px-2 py-2 rounded flex items-center justify-center text-blue-600 hover:text-blue-800"
          title="Capture d'écran"
          aria-label="Capture d'écran Dashboard"
        >
          <Camera className="w-5 h-5" />
        </button>

        
        <button
          onClick={async () => {
            try {
              toast.loading('Génération et copie du lien...', { id: 'link-db' });
              const body = document.body;
              const htmlEl = document.documentElement;
              const fullHeight = Math.max(body.scrollHeight, body.offsetHeight, htmlEl.clientHeight, htmlEl.scrollHeight, htmlEl.offsetHeight);
              const canvas = await html2canvas(body, {
                scrollY: -window.scrollY,
                scrollX: -window.scrollX,
                height: fullHeight,
                windowHeight: fullHeight,
                width: htmlEl.scrollWidth,
                scale: 1,
                useCORS: true,
                allowTaint: true,
                logging: false
              });
              const dataUrl = canvas.toDataURL('image/png');
              const links = Array.from(document.querySelectorAll('a[href]'))
                .map((a) => {
                  try {
                    const r = a.getBoundingClientRect();
                    const left = r.left + window.scrollX;
                    const top = r.top + window.scrollY;
                    return {
                      href: a.href,
                      target: a.target || '_blank',
                      left: Math.round(left),
                      top: Math.round(top),
                      width: Math.round(r.width),
                      height: Math.round(r.height)
                    };
                  } catch (e) {
                    return null;
                  }
                })
                .filter(Boolean)
                .filter(l => l.width > 2 && l.height > 2);

              const winHtmlParts = [];
              winHtmlParts.push('<!doctype html><html><head><meta charset="utf-8"><title>Snapshot avec liens</title>');
              winHtmlParts.push('<meta name="viewport" content="width=device-width, initial-scale=1">');
              winHtmlParts.push('<style>body{margin:0;background:#111;color:#111} .container{position:relative;}\nimg{display:block;border:0}\n.overlay{position:absolute;display:block;background:transparent}\n.overlay:hover{outline:2px solid rgba(59,130,246,0.35)}\n</style></head><body>');
              winHtmlParts.push(`<div class="container" style="width:${canvas.width}px;height:${canvas.height}px">`);
              winHtmlParts.push(`<img src="${dataUrl}" width="${canvas.width}" height="${canvas.height}" alt="screenshot"/>`);
              const scaleX = canvas.width / (htmlEl.scrollWidth || canvas.width);
              const scaleY = canvas.height / (fullHeight || canvas.height);
              const overlayVerticalOffset = -58;
              links.forEach((l, idx) => {
                const href = String(l.href).replace(/"/g, '%22');
                const leftS = Math.round(l.left * scaleX);
                const topS = Math.round(l.top * scaleY) + overlayVerticalOffset;
                const widthS = Math.max(1, Math.round(l.width * scaleX));
                const heightS = Math.max(1, Math.round(l.height * scaleY));
                winHtmlParts.push(`<a class="overlay" href="${href}" target="${l.target}" style="left:${leftS}px;top:${topS}px;width:${widthS}px;height:${heightS}px" title="Lien ${idx+1}"></a>`);
              });
              winHtmlParts.push('</div></body></html>');
              const htmlContent = winHtmlParts.join('\n');

              const blob = new Blob([htmlContent], { type: 'text/html' });
              const url = URL.createObjectURL(blob);
              await navigator.clipboard.writeText(url);
              // keep URL alive for a while; do not revoke immediately
              setTimeout(() => { try { URL.revokeObjectURL(url); } catch(e){} }, 1000 * 60 * 10); // revoke after 10 minutes
              toast.success('Lien du snapshot copié dans le presse-papier', { id: 'link-db' });
            } catch (err) {
              console.error('Erreur copie lien snapshot:', err);
              toast.error('Erreur lors de la copie du lien', { id: 'link-db' });
            }
          }}
          className="px-2 py-2 rounded flex items-center justify-center text-blue-600 hover:text-blue-800"
          title="Copier le lien du snapshot"
          aria-label="Copier le lien snapshot Dashboard"
        >
          <Copy className="w-5 h-5" />
        </button>

        <button
          onClick={handleSendToSlack}
          disabled={isSendingToSlack}
          className={`px-2 py-2 rounded flex items-center justify-center ${
            isSendingToSlack 
              ? 'text-gray-400 cursor-not-allowed' 
              : 'text-green-600 hover:text-green-800'
          }`}
          title="Envoyer vers Slack"
          aria-label="Envoyer vers Slack Dashboard"
        >
          {isSendingToSlack ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
      

      {/* Loading overlay */}
      {isVersionLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-3">
            <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
            <span className="text-sm font-medium">Chargement des données...</span>
          </div>
        </div>
      )}

      
      {/* Version Selector & Statut de l'environnement */}
      <div className="grid grid-cols-1 gap-1">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Version selector */}
            <div className="flex items-center gap-3">
              <label htmlFor="version-select" className="text-sm font-medium text-gray-700">Version</label>
              <select
                id="version-select"
                value={selectedVersion}
                onChange={(e) => {
                  const value = e.target.value;
                  setIsVersionLoading(true);
                  setSelectedVersion(value);
                  try { localStorage.setItem('selectedVersion', value); } catch {}
                  // Synchroniser l'URL pour conserver le contexte
                  navigate(`/?version=${value}`);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="develop">25.2</option>
                <option value="25.1">25.1</option>
              </select>
            </div>

            {/* Statut de l'environnement */}
            <div className="text-center md:text-right flex-1">
              <p className="text-lg font-medium text-gray-600">Statut de la version</p>
              {(() => {
                // Pour la version 25.1, on ne considère pas les bugs dans le statut
                const bugcritical = selectedVersion === '25.1' ? 0 : allBugs.filter(p => String(p.severity) === String("1 - Critical")).length;
                const status = getEnvStatusFromPercentage(globalStats.averageSuccessRate, bugcritical);
                const bg = status.color === 'green' ? 'bg-green-100 text-green-800' : status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';
                const icon = status.color === 'green'
                  ? <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  : status.color === 'yellow'
                    ? <AlertTriangle className="w-4 h-4 mr-2 text-yellow-600" />
                    : <XCircle className="w-4 h-4 mr-2 text-red-600" />;

                return (
                  <div className="mt-2 flex justify-center md:justify-end">
                    <span className={`inline-flex items-center px-7 py-1 rounded-full text-base font-medium ${bg}`}>
                      {icon}
                      {status.label.charAt(0).toUpperCase() + status.label.slice(1)}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Tests Passés</p>
              <p className="text-2xl font-bold text-green-600">{globalStats.totalPassed}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Tests Échoués</p>
              <p className="text-2xl font-bold text-red-600">{globalStats.totalFailed}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Tests Ignorés</p>
              <p className="text-2xl font-bold text-gray-500">{globalStats.totalSkipped || 0}</p>
            </div>
            <Clock className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Taux de Réussite</p>
              <p className="text-2xl font-bold text-blue-600">{typeof globalStats.averageSuccessRate === 'number'? globalStats.averageSuccessRate.toFixed(1): '0.0'}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Durée Moyenne</p>
              <p className="text-2xl font-bold text-purple-600">{formatDuration(globalStats.averageDuration)|| 0}</p>
            </div>
            <Clock className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>



      {/* Pipelines Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {pipelines.map((pipeline) => (
          <div key={pipeline.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{pipeline.name}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleTriggerPipeline(pipeline.id)}
                  disabled={triggerLoading.has(pipeline.id)}
                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200 disabled:opacity-50 transition-colors"
                  title="Lancer le pipeline"
                >
                  {triggerLoading.has(pipeline.id) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <StatusBadge status={pipeline.status} />
              </div>
            </div>
            
            <div className="space-y-3">
              {pipeline.executionDate && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Date d'exécution:</span>
                  <span className="font-medium">{formatDate(pipeline.executionDate)}</span>
                </div>
              )}
              {pipeline.testEnvironment && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Environnement de test:</span>
                  <span className="font-medium">{pipeline.testEnvironment}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Dernier Build:</span>
                <span className="font-medium">{pipeline.lastBuildId}</span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tests:</span>
                <span className="font-medium">
                  <span className="text-green-600">{pipeline.passedTests}</span> / 
                  <span className="text-red-600">{pipeline.failedTests}</span> / 
                  {pipeline.skippedTests > 0 && <><span className="text-gray-500">{pipeline.skippedTests}</span> / </>}
                  <span className="text-gray-900">{pipeline.totalTests}</span>
                </span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Durée:</span>
                <span className="font-medium">{formatDuration(pipeline.totalDuration)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <p className="text-sm font-medium text-gray-600">Taux de réussite:</p>
                <p className="text-2xl font-bold text-blue-600">{groupAverages[pipeline.name] ? Number(groupAverages[pipeline.name]).toFixed(1) : '0.0'}%</p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${(pipeline.successRate || 0) >= 90 ? 'bg-green-500' : (pipeline.successRate || 0) >= 70 ? 'bg-yellow-400' : 'bg-red-500'}`}
                  style={{ width: `${pipeline.successRate || 0}%` }}
                ></div>
              </div>
              
              <button
                onClick={() => navigate(`/pipeline/${pipeline.id}?version=${selectedVersion}`)}
                className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                <Eye className="w-4 h-4" />
                Voir Détails
              </button>
            </div>
          </div>
        ))}
      </div>
            
      {/* Bugs lists - Only show for develop version */}
      {selectedVersion === 'develop' && (
        <div className="grid grid-cols-1 gap-1">
          {/* Data Preparation Bugs */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-red-600">New TA Regression - All squads 
                <span className="text-sm text-gray-600"> ({allBugs.length}) </span>
                </h3>
            </div>


            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm text-gray-600">Détail des criticités 
                <span className="text-red-600"> ({allBugs.filter(p => String(p.severity) === String("1 - Critical")).length} bugs critiques - </span>
                <span className="text-gray-600"> {allBugs.filter(p => String(p.severity) === String("2 - High")).length} bugs High - </span>
                <span className="text-gray-600"> {allBugs.filter(p => String(p.severity) === String("3 - Medium")).length} bugs Medium - </span>
                <span className="text-gray-600"> {allBugs.filter(p => String(p.severity) === String("4 - Low")).length} bugs Low)</span>
               </h3>
            </div>
            {allBugs.length === 0 ? (
              <div className="text-gray-600">Aucun bug trouvé.</div>
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                {allBugs.map(b => (
                  <div key={b.id} className="border rounded p-3 flex justify-between items-start">
                    <div>
                      <a href={b.url} target="_blank" rel="noreferrer" className="text-gray-600 font-medium hover:underline">{b.title}</a>
                      <div className="text-sm text-gray-600 mt-1">Etat: {b.state} • Tags: {b.tags || '—'}</div>
                      <div className="text-sm text-gray-600 mt-1">Area: {b.areaPath}</div>
                      <div className="text-sm text-gray-500">Assigned: {b.assignedTo || '—'} • Modifié: {b.changedDate ? formatDate(b.changedDate) : '—'}</div>
                    </div>
                    <div className="text-right text-sm">
                      {b.severity && <div className="mb-2">Sévérité: {b.severity}</div>}
                      <a href={b.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600">Ouvrir <ExternalLink className="w-4 h-4"/></a>
                    </div>
                  </div>
                ))}
              </div>
            )}
                          <button
                  onClick={() => setShowBugsModal(true)}
                  className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Liste des bugs
                </button>
          </div>

        </div>
      )}

      {/* Modal Liste des bugs */}
      {showBugsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h3 className="text-lg font-semibold text-red-600">
                  New TA Regression - All squads 
                  <span className="text-sm text-gray-600"> ({allBugs.length}) </span>
                </h3>
                <h3 className="text-sm text-gray-600 mt-2">
                  Détail des criticités 
                  <span className="text-red-600"> ({allBugs.filter(p => String(p.severity) === String("1 - Critical")).length} bugs critiques - </span>
                  <span className="text-gray-600"> {allBugs.filter(p => String(p.severity) === String("2 - High")).length} bugs High - </span>
                  <span className="text-gray-600"> {allBugs.filter(p => String(p.severity) === String("3 - Medium")).length} bugs Medium - </span>
                  <span className="text-gray-600"> {allBugs.filter(p => String(p.severity) === String("4 - Low")).length} bugs Low)</span>
                </h3>
                <div className="mt-2">
                  <a 
                    href="https://dev.azure.com/chapsvision/Fabrique/_queries/query/67020172-e295-4bb7-981f-87758ab02f7d/" 
                    target="_blank" 
                    rel="noreferrer" 
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  >
                    Query Azure DevOps <ExternalLink className="w-4 h-4"/>
                  </a>
                </div>
              </div>
              <button
                onClick={() => setShowBugsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {allBugs.length === 0 ? (
                <div className="text-gray-600 text-center py-8">Aucun bug trouvé.</div>
              ) : (
                <div className="space-y-3">
                  {allBugs.map(b => (
                    <div key={b.id} className="border rounded p-3 flex justify-between items-start hover:bg-gray-50 transition-colors">
                      <div className="flex-1">
                        <a href={b.url} target="_blank" rel="noreferrer" className="text-gray-600 font-medium hover:underline">
                          {b.title}
                        </a>
                        <div className="text-sm text-gray-600 mt-1">Etat: {b.state} • Tags: {b.tags || '—'}</div>
                        <div className="text-sm text-gray-600 mt-1">Area: {b.areaPath}</div>
                        <div className="text-sm text-gray-500">
                          Assigned: {b.assignedTo || '—'} • Modifié: {b.changedDate ? formatDate(b.changedDate) : '—'}
                        </div>
                      </div>
                      <div className="text-right text-sm ml-4">
                        {b.severity && <div className="mb-2 font-medium">Sévérité: {b.severity}</div>}
                        <a 
                          href={b.url} 
                          target="_blank" 
                          rel="noreferrer" 
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          Ouvrir <ExternalLink className="w-4 h-4"/>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowBugsModal(false)}
                className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trends Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Évolution des Tests</h3>
          <div className="flex items-center gap-1">
            {[7, 14, 21].map(d => (
              <button key={d} onClick={() => setTrendDays(d)}
                className={`px-2 py-0.5 text-xs rounded transition-colors ${trendDays === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                {d}j
              </button>
            ))}
          </div>
        </div>
        {trendLoading ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-400">Chargement...</div>
        ) : Object.values(trendData).some(arr => arr.length > 0) ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pipelineConfigs[selectedVersion].pipelinesList.map(pipeline => {
              const data = trendData[pipeline.id] || [];
              const lastRun = data[data.length - 1];
              const successRate = lastRun && lastRun.total > 0 ? Math.round((lastRun.success / lastRun.total) * 100) : null;
              const maxTotal = data.length > 0 ? Math.max(...data.map(d => d.total)) : 0;
              return (
                <div key={pipeline.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-800">{pipeline.name}</span>
                    {successRate !== null && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${successRate >= 90 ? 'bg-green-100 text-green-700' : successRate >= 70 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                        {successRate}%
                      </span>
                    )}
                  </div>
                  {data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 40, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="sessionLabel" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" />
                        <YAxis tick={{ fontSize: 9 }} width={28} />
                        <Tooltip content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div className="bg-white border border-gray-200 rounded shadow p-2 text-xs">
                              <p className="font-semibold">{d.sessionLabel}</p>
                              {d.buildNumber && <p className="text-gray-400">Build {d.buildNumber}</p>}
                              <p className="text-green-600">✓ {d.success}</p>
                              <p className="text-red-500">✗ {d.failed}</p>
                              <p className="text-gray-600">= {d.total}</p>
                            </div>
                          );
                        }} />
                        {maxTotal > 0 && <ReferenceLine y={maxTotal} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: `${maxTotal}`, position: 'insideTopRight', fontSize: 9, fill: '#f59e0b' }} />}
                        <Bar dataKey="success" stackId="a" fill="#10b981" />
                        <Bar dataKey="failed" stackId="a" fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-40 flex items-center justify-center text-xs text-gray-400">Aucune exécution</div>
                  )}
                  {lastRun && <p className="text-xs text-gray-400 mt-1">Dernier run : {lastRun.sessionLabel}</p>}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="h-40 flex items-center justify-center text-sm text-gray-500">
            Aucune exécution sur la période sélectionnée.
          </div>
        )}
      </div>
    </div>
  );
};

// Pipeline Details Component
const PipelineDetails = ({ pipelineId, navigate, version = 'develop', configMode = 'default' }) => {
  const [pipelines, setPipelines] = useState([]);
  const [pBuilds, setBuilds] = useState([]);
  const pipelineConfigs = getPipelineConfigs(configMode);

  useEffect(() => {
    const fetchData = async () => {
      const config = pipelineConfigs[version];
      const pipelinesData = await azureDevOpsService.getBuildsData(config.pipelinesList);
      setPipelines(pipelinesData);      
    };

    fetchData();
  }, [version]);

  useEffect(() => {
    const fetchBuildData = async () => {
      // Vérifier d'abord si le pipeline existe dans la version actuelle
      const pipelineConfig = pipelineConfigs[version].pipelinesList.find(p => String(p.id) === String(pipelineId));
      
      if (!pipelineConfig) {
        console.log('❌ Pipeline not found in version', version, '- redirecting to dashboard');
        const configQuery = configMode === 'new' ? `&config=${configMode}` : '';
        navigate(`/?version=${version}${configQuery}`);
        return;
      }

      try {
        const buildsData = await azureDevOpsService.getRecentBuildsByPipeline(pipelineId);

        setBuilds(buildsData);
        
        // Redirection automatique vers le dernier build (le plus récent)
        const builds = buildsData[`pipeline-${String(pipelineId)}`] || [];
        
        if (builds.length > 0) {
          const latestBuild = builds[0]; // Le premier build est normalement le plus récent
          const targetUrl = `/pipeline/${pipelineId}/build/${latestBuild.buildId}?version=${version}`;
          navigate(targetUrl);
          return; // Sortir de la fonction pour éviter de continuer le rendu
        }
        
        console.log('⚠️ No builds found for pipeline', pipelineId);
      } catch (error) {
        console.error("Erreur chargement des builds:", error);
      }
    };

    fetchBuildData();
  }, [pipelineId, navigate]);

  

  // Message d'attente pendant la redirection
  return (
    <div className="flex justify-center items-center h-64">
      {/* <div className="text-gray-600">Redirection vers le dernier build...</div> */}
    </div>
  );
};

// Build Details Component
const BuildDetails = ({ pipelineId, buildId, navigate, version = 'develop', configMode = 'default' }) => {
  const pipelineConfigs = getPipelineConfigs(configMode);
  const [pipelines, setPipelines] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [pBuilds, setBuilds] = useState([]);
  const [activeTab, setActiveTab] = useState('suites'); // Par défaut sur les test suites
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // No auto-restoration, no need to clear storage

  // Init tab from URL
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const tab = sp.get('tab');
    if (tab && ['suites','history','table'].includes(tab)) setActiveTab(tab);
  }, [buildId, pipelineId]);

  // Reflect tab in URL using replaceState to avoid remount flicker
  useEffect(() => {
    if (!buildId || !pipelineId) return;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('tab') === activeTab) return; // no change
    sp.set('tab', activeTab);
    sp.set('version', version);
    if (configMode === 'new') {
      sp.set('config', configMode);
    } else {
      sp.delete('config');
    }
    const newSearch = `?${sp.toString()}`;
    const basePath = `/readyapi/pipeline/${pipelineId}/build/${buildId}`;
    const target = basePath + newSearch;
    if (window.location.pathname + window.location.search !== target) {
      window.history.replaceState(null, '', target);
    }
  }, [activeTab, buildId, pipelineId, version]);

  useEffect(() => {
    const fetchAll = async () => {
      try {      
        setLoading(true);
        const [testSuiteData, pipelinesData, buildsData, trendBuilds] = await Promise.all([
          azureDevOpsService.getTestSuitesForBuild(buildId),
          azureDevOpsService.getBuildsData(pipelineConfigs[version].pipelinesList),
          azureDevOpsService.getRecentBuildsByPipeline(pipelineId),
          azureDevOpsService.getTrendDataFromBuilds(pipelineId)
        ]);

        const suitesArray = testSuiteData[`build-${buildId.replace('build-', '')}`] || [];
        setTestSuites(suitesArray);
        setPipelines(pipelinesData);
        setBuilds(buildsData);
        setTrendData(trendBuilds);
        setLoading(false);
        setError(null);
      } catch (error) {
        console.error('Erreur chargement des données:', error);
        setError('Impossible de charger les données');
        setLoading(false);
      }
    };
    fetchAll();
  }, [buildId, pipelineId]);

  // Find the correct pipeline from the version-specific configuration
  const pipelineConfig = pipelineConfigs[version].pipelinesList.find(p => String(p.id) === String(pipelineId));
  const pipeline = pipelineConfig ? pipelines.find((p) => String(p.id) === String(pipelineId)) : null;
  const pipelineName = (pipeline && pipeline.name) || (pipelineConfig && pipelineConfig.name) || `Pipeline ${pipelineId}`;
  const builds = pBuilds[`pipeline-${String(pipelineId)}`] || [];

  // Inline loading & error handling to avoid full component swap flicker
  const buildError = error ? (
    <div className="text-center py-4 text-red-600">{error}</div>
  ) : null;
  const buildStatsSkeleton = (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 animate-pulse">
      <div className="bg-gray-100 rounded-lg p-4 h-20" />
      <div className="bg-gray-100 rounded-lg p-4 h-20" />
      <div className="bg-gray-100 rounded-lg p-4 h-20" />
    </div>
  );
  // Gérer le paramètre returnTo pour rediriger vers une page spécifique
  const urlParams = new URLSearchParams(window.location.search);
  const returnTo = urlParams.get('returnTo') || getStoredReturnTo();
  const handleReturn = () => {
    if (returnTo) {
      window.location.href = returnTo;
    } else {
      navigate(`/?version=${version}`);
    }
  };

  if (!pipelineConfig) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">Pipeline {pipelineId} non trouvée dans la version {version}</div>
        <button
          onClick={handleReturn}
          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
        >
          ← Retour
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{buildId}</h2>
            <p className="text-gray-600">{pipelineName}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReturn}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
            >
              ← Retour
            </button>
          </div>
        </div>
        
        {/* Statistiques du build actuel */}
        {loading ? buildStatsSkeleton : buildError || (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Test Suites</span>
                <span className="text-lg font-bold text-blue-600">{testSuites.length}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Tests Passés</span>
                <span className="text-lg font-bold text-green-600">{testSuites.reduce((sum, suite) => sum + suite.passed, 0)}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Tests Échoués</span>
                <span className="text-lg font-bold text-red-600">{testSuites.reduce((sum, suite) => sum + suite.failed, 0)}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Tests Ignorés</span>
                <span className="text-lg font-bold text-gray-500">{testSuites.reduce((sum, suite) => sum + (suite.skipped || 0), 0)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('suites')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'suites'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Test Suites
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Historique Builds
            </button>
            <button
              onClick={() => setActiveTab('table')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'table'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Vue Tableau
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Onglet Test Suites - Vue par défaut */}
          {activeTab === 'suites' && !loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testSuites.map((suite) => (
                <div key={suite.runId} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{suite.name}</h4>
                    <StatusBadge status={suite.failed > 0 ? 'failed' : 'success'} />
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-medium">{suite.totalTests} tests</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Passés:</span>
                      <span className="font-medium text-green-600">{suite.passed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Échoués:</span>
                      <span className="font-medium text-red-600">{suite.failed}</span>
                    </div>
                    {suite.skipped > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ignorés:</span>
                        <span className="font-medium text-gray-500">{suite.skipped}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Durée:</span>
                      <span className="font-medium">{formatDuration(suite.duration)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/pipeline/${pipelineId}/build/${buildId}/run/${suite.runId}/testsuite/${suite.name}?version=${version}`)}
                    className="w-full mt-3 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    Voir Détails
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Onglet Historique des Builds */}
          {activeTab === 'history' && !loading && !error && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des 5 derniers builds</h3>
              <div className="divide-y divide-gray-200">
                {builds.map((build) => (
                  <div key={build.buildId} className={`p-6 hover:bg-gray-50 transition-colors ${
                    build.buildId === buildId ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <StatusBadge status={build.status} />
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {build.buildId}
                            {build.buildId === buildId && (
                              <span className="ml-2 text-sm text-blue-600 font-normal">(Actuel)</span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600">{formatDate(build.date)}</p>
                        </div>
                      </div>

                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tests:</span>
                        <span className="font-medium ml-2">
                          <span className="text-green-600">{build.passedTests}</span> / 
                          <span className="text-red-600">{build.failedTests}</span> / 
                          {build.skippedTests > 0 && <><span className="text-gray-500">{build.skippedTests}</span> / </>}
                          <span className="text-gray-900">{build.totalTests}</span>
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600">{formatDuration(build.duration)}</span>
                        {build.buildId !== buildId && (
                          <button
                            onClick={() => navigate(`/pipeline/${pipelineId}/build/${build.buildId}?version=${version}`)}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                          >
                            Voir ce Build
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Status Evolution Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des Statuts</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="success" stroke="#10b981" strokeWidth={2} name="Succès" />
                    <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} name="Échecs" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Onglet Vue Tableau */}
          {activeTab === 'table' && !loading && !error && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suite</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nb Tests</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Échoués</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ignorés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {testSuites.map((suite) => (
                    <tr key={suite.runId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{suite.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{suite.totalTests}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-green-600 font-medium">{suite.passed}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-red-600 font-medium">{suite.failed}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-medium">{suite.skipped || 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatDuration(suite.duration)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/pipeline/${pipelineId}/build/${buildId}/run/${suite.runId}/testsuite/${suite.name}?version=${version}`)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                        >
                          Détails
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// Test Suite Details Component
const TestSuiteDetails = ({ pipelineId, buildId, runId, testSuiteName, navigate, version = 'develop', initialSearchTerm = '' }) => {
  // No automatic restoration mechanism - returnUrl handles history navigation
  const getStepNameFromTestSuite =(testSuiteName) => {
    switch (testSuiteName) {
      // Version develop - ancien format
      case 'DP - smoke tests':
        return 'Run Smoke Tests';
      case 'DP - Regression Test':
        return 'Run Regression Test';
      case 'DP - Absimilia Test Processus':
        return 'Run Absimilia Test Suite';
      case 'DP - Clean Up DP':
        return 'Run Clean Up Test Suite';
      case 'DE - Cleanup':
        return 'Run Cleanup';
      case 'DE - smoke tests':
        return 'Run Smoke Tests';
      case 'DE - Regression Tests Explo':
        return 'Run Regression Suite';
      case 'DE - Admin':
        return 'Run  Suite Admin Explo';
      case 'Framework - smoke tests':
        return 'Run Smoke Tests';
      case 'Framework - Regression Test':
        return 'Run Test Regression';
      case 'Inv - Critical Test':
        return 'Run Critical Test';
      case 'Inv - Major Test':
        return 'Run Major Test';
      
      // Version 25.1 - nouveau format
      case 'Regression Test DP':
        return 'Run Regression Test';
      case 'smoke tests result DP':
        return 'Run Smoke Tests';
      case 'Absimilia Test Processus':
        return 'Run Absimilia Test Suite';
      case 'Clean Up DP':
        return 'Run Clean Up Test Suite';
      case 'Regression Tests Explo':
        return 'Run Regression Suite';
      case 'smoke tests result':
        return 'Run Smoke Tests';
      case 'Suite Admin Explo':
        return 'Run  Suite Admin Explo';
      case 'Cleanup result DE':
        return 'Run Cleanup';
      case 'Regression Test Admin':
        return 'Run Test Regression';
      case 'smoke tests result Admin':
        return 'Run Smoke Tests';
        
      default:
        throw new Error(`Test suite inconnue : ${testSuiteName}`);
    }
  };
  const [runningTests, setRunningTests] = useState({}); 
  // Persist and resume relaunch statuses across navigation (robust, normalized key + global map)
  const normalizeSuiteKey = (s) => {
    try { return decodeURIComponent(String(s || '')).trim().toLowerCase(); } catch { return String(s || '').trim().toLowerCase(); }
  };
  const suiteKey = useMemo(() => normalizeSuiteKey(testSuiteName), [testSuiteName]);
  const relaunchStorageKey = useMemo(
    () => `readyapi:relaunches:${version}:${suiteKey}`,
    [version, suiteKey]
  );
  const GLOBAL_RELAUNCHES_KEY = 'readyapi:relaunches:v1';
  // Global tracker subscription (single source of truth across pages)
  
  // Start global tracker and subscribe to updates for this suite
  useEffect(() => {
    relaunchTracker.start();
    // Prime local state
    setRunningTests(relaunchTracker.getEntriesFor(version, suiteKey));
    const unsub = relaunchTracker.subscribe(() => {
      setRunningTests(relaunchTracker.getEntriesFor(version, suiteKey));
    });
    return () => unsub();
  }, [version, suiteKey]);
  
  // Local persistence replaced by global tracker
  
  // Background polling handled by global tracker
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [relaunchStatuses, setRelaunchStatuses] = useState({});
  const [logInput, setLogInput] = useState('');
  // Fetch des logs (version fonctionnelle originale)
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLogsLoading(true);
        const stepName = getStepNameFromTestSuite(testSuiteName);
        const log = await azureDevOpsService.getTestLogsFromBuild(buildId, stepName);
        if (log && log[0] && log[0].content) {
          setLogInput(log[0].content);
        } else {
          setLogInput('');
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des logs :', err);
        setError(err.message);
      } finally {
        setLogsLoading(false);
      }
    };

    fetchLogs();
  }, [buildId, testSuiteName]);

  // INTERVAL DÉSACTIVÉ TEMPORAIREMENT - CAUSAIT BOUCLE INFINIE
  // TODO: Réimplémenter avec useRef ou sans setInterval
  /*
  useEffect(() => {
    const interval = setInterval(async () => {
      // Surveillance du statut des tests relancés
    }, 10000);
    return () => clearInterval(interval);
  }, []);
  */

  const handleRelancer = async (testName) => {
    toast.loading(`Relance du test ${testName} en cours...`, { id: testName });

    const result = await azureDevOpsService.launchTestCase(testName, testSuiteName);
    if (result.success) {
      toast.success(`Test ${testName} relancé`, { id: testName });
      // Build next state and persist immediately to avoid losing it on quick navigation
      setRunningTests(prev => {
        const next = {
          ...prev,
          [testName]: {
            status: 'inProgress',
            runId: result.runId,
            runUrl: result.runUrl,
            startedAt: new Date().toISOString(),
            result: null,
          }
        };
        // Also update the global tracker so it keeps polling even if we leave this page
        relaunchTracker.upsertRelaunch(version, suiteKey, testName, next[testName]);
        return next;
      });
    } else {
      toast.error(`Erreur lors du déclenchement du test`, { id: testName });
    }
  };

  const getRelanceStatus = (testName) => {
    const relance = runningTests[testName];
    if (!relance) return null;

    if (relance.status === 'inProgress') {
      return (
        <a 
          href={relance.runUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center space-x-2"
        >
          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-yellow-600 font-medium">En cours...</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      );
    }

    if (relance.status === 'completed') {
      if (relance.result === 'succeeded') {
        return (
          <a 
            href={relance.runUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center space-x-2 text-green-600 hover:text-green-800 hover:underline transition-colors"
          >
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium">Succès</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        );
      } else {
        return (
          <a 
            href={relance.runUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center space-x-2 text-red-600 hover:text-red-800 hover:underline transition-colors"
          >
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-sm font-medium">Échec</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        );
      }
    }

    return (
      <span className="text-sm text-gray-400">-</span>
    );
  };

  // Open Trace Logs modal and fetch via server proxy
  const openTraceModal = async (traceId) => {
    try {
      // La zone suit l'environnement du build (cf. services/traceZone.js).
      // Le nom de la suite n'est plus qu'un repli pour un build qui n'en
      // declare aucun : il envoyait une suite DP lancee sur val05 dans qa3.
      const name = String(testSuiteName || '').toLowerCase();
      const zone = zoneDuBuild || ((name.includes('dp') || name.includes('investigation')) ? 'qa3' : 'qa1');
      setElasticZone(zone);
      
      // Check if credentials exist
      const credentials = elasticAuthService.getCredentials(zone);
      if (!credentials) {
        setPendingTraceId(traceId);
        setShowElasticAuthModal(true);
        return;
      }
      
      setSelectedTraceId(traceId);
      setShowTraceModal(true);
      setTraceLoading(true);
      setTraceResult(null);
      setTraceError(null);
      
      const resp = await fetch('/api/logs/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ traceId, zone, auth: credentials.token })
      });
      const contentType = resp.headers.get('content-type') || '';
      let data;
      if (contentType.includes('application/json')) {
        data = await resp.json();
      } else {
        data = await resp.text();
      }
      if (!resp.ok) {
        // Si 401, credentials invalides -> les supprimer et redemander
        if (resp.status === 401) {
          elasticAuthService.clearCredentials(zone);
          setPendingTraceId(traceId);
          setShowElasticAuthModal(true);
          setTraceError('Authentification échouée. Veuillez vérifier vos credentials.');
          return;
        }
        throw new Error(typeof data === 'string' ? data : (data.error || 'Erreur upstream'));
      }
      setTraceResult(data);
    } catch (e) {
      setTraceError(e.message || String(e));
    } finally {
      setTraceLoading(false);
    }
  };

  // Handle Elastic authentication
  const handleElasticAuth = async (username, password) => {
    elasticAuthService.saveCredentials(elasticZone, username, password);
    setShowElasticAuthModal(false);
    setTraceError(null); // Réinitialiser l'erreur
    if (pendingTraceId) {
      await openTraceModal(pendingTraceId);
      setPendingTraceId(null);
    }
  };

  // Fonction pour extraire les logs d'un test case spécifique
  const extractTestCaseLog = (fullLogs, testCaseName) => {
    const startPattern = new RegExp(`Running TestCase \\[${testCaseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`);
    const endPattern = new RegExp(`Finished running TestCase \\[${testCaseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`);
    
    const lines = fullLogs.split('\n');
    let inside = false;
    const extracted = [];
    
    for (const line of lines) {
      if (!inside && startPattern.test(line)) {
        inside = true;
      }
      if (inside) {
        extracted.push(line);
        if (endPattern.test(line)) {
          break;
        }
      }
    }
    
    if (extracted.length === 0) {
      return `[Logs] Aucun log trouvé pour le test case: ${testCaseName}`;
    }
    
    return extracted.join('\n');
  };

  const parseReadyAPILogs = (logs) => {
    const lines = logs.split('\n').filter(line => line.trim());
    const testSuites = [];
    let currentSuite = null;
    let currentTestCase = null;
    let currentStep = null;
    let nestedTestCase = null;
    let nestedStep = null;
    let stepDetails = []; // Détails temporaires collectés entre événements
    let testCaseStack = []; // Stack pour gérer la hiérarchie des test cases
    let collectingErrorMessage = false; // Flag pour collecter les lignes d'erreur multi-lignes
    let errorMessageBuffer = ''; // Buffer pour accumuler le message d'erreur

    const toMs = (t) => {
      // t = HH:MM:SS,mmm -> ms depuis minuit
      const m = t.match(/^(\d{2}):(\d{2}):(\d{2}),(\d{3})$/);
      if (!m) return null;
      const [_, hh, mm, ss, ms] = m;
      return (+hh) * 3600000 + (+mm) * 60000 + (+ss) * 1000 + (+ms);
    };

    const normalizeAssertStatus = (s) => {
      // Convertit VALID/INVALID/FAILED en PASS/FAIL si nécessaire
      if (!s) return s;
      const up = String(s).toUpperCase();
      if (up === 'VALID' || up === 'PASSED') return 'PASS';
      if (up === 'INVALID' || up === 'FAILED') return 'FAIL';
      return up;
    };

    const pushDetail = (targetArray, line, message, level = 'INFO') => {
      targetArray.push({
        time: extractTime(line),
        level,
        message: message?.trim() || ''
      });
    };

    lines.forEach(line => {
      // Si on est en train de collecter un message d'erreur multi-lignes
      if (collectingErrorMessage) {
        // Vérifier si on atteint une nouvelle ligne d'événement SoapUI
        if (line.includes('[SoapUIProTestCaseRunner]') && (line.includes('running step') || line.includes('Running TestCase') || line.includes('Finished running') || line.includes('Assertion'))) {
          // Fin du message d'erreur, l'assigner
          if (currentStep) {
            const lastAssertion = currentStep.assertions[currentStep.assertions.length - 1];
            if (lastAssertion && lastAssertion.status === 'FAIL') {
              lastAssertion.errorMessage = errorMessageBuffer.trim();
            } else {
              if (!currentStep.errorDetails) currentStep.errorDetails = [];
              const existing = currentStep.errorDetails[currentStep.errorDetails.length - 1];
              if (existing) {
                existing.message = errorMessageBuffer.trim();
              }
            }
          }
          collectingErrorMessage = false;
          errorMessageBuffer = '';
          // Ne pas return, continuer à traiter cette ligne
        } else {
          // Continuer à collecter le message d'erreur
          const trimmed = line.trim();
          if (trimmed) {
            errorMessageBuffer += '\n' + trimmed;
          }
          return;
        }
      }

      // Extraire potentiels indicateurs HTTP pour un step
      if (currentStep) {
        const respTime = line.match(/response time:?\s*(\d+)\s*ms/i);
        if (respTime) {
          currentStep.responseTimeMs = Number(respTime[1]);
        }
        const statusCode = line.match(/\b(?:status code|response status|http\/?1\.[01])\D*(\d{3})\b/i);
        if (statusCode) {
          currentStep.statusCode = Number(statusCode[1]);
        }
      }

      // Ignorer les lignes qui ne concernent pas SoapUI runner
      if (!line.includes('[SoapUIProTestCaseRunner]')) {
        return;
      }

      // Test Suite start
      if (line.includes('Running Test Suite')) {
        const match = line.match(/Running Test Suite \[([^\]]+)\]/);
        if (match) {
          currentSuite = {
            name: match[1],
            status: 'RUNNING',
            testCases: [],
            startTime: extractTime(line)
          };
          testSuites.push(currentSuite);
          testCaseStack = [];
        }
      }

      // Test Case start
      else if (line.includes('Running TestCase')) {
        const match = line.match(/Running TestCase \[([^\]]+)\]/);
        if (match && currentSuite) {
          const testCaseData = {
            name: match[1],
            status: 'RUNNING',
            steps: [],
            startTime: extractTime(line),
            isNested: testCaseStack.length > 0
          };

          if (testCaseStack.length === 0) {
            currentSuite.testCases.push(testCaseData);
            currentTestCase = testCaseData;
          } else {
            if (currentStep) {
              if (!currentStep.nestedTestCases) currentStep.nestedTestCases = [];
              currentStep.nestedTestCases.push(testCaseData);
            }
            nestedTestCase = testCaseData;
          }

          testCaseStack.push(testCaseData);
        }
      }

      // Step start
      else if (line.includes('running step')) {
        const match = line.match(/running step \[([^\]]+)\]/);
        if (match) {
          // Fermer le step précédent en estimant la fin à l'instant présent
          const nowT = extractTime(line);
          if (currentStep && currentStep.startTime) {
            currentStep.endTime = nowT;
            const d = toMs(nowT) - toMs(currentStep.startTime);
            if (!isNaN(d)) currentStep.durationMs = Math.max(0, d);
          }

          const stepData = {
            name: match[1],
            status: 'RUNNING',
            assertions: [],
            startTime: nowT
          };

          // Reset des détails accumulés
          stepDetails = [];

          // Ajouter au test case courant
          const activeTestCase = testCaseStack[testCaseStack.length - 1];
          if (activeTestCase) {
            activeTestCase.steps.push(stepData);
            currentStep = stepData;
            if (testCaseStack.length > 1) {
              nestedStep = stepData;
            } else {
              nestedStep = null;
            }
          }
        }
      }

      // Assertion status
      else if (line.includes('Assertion') && line.includes('has status')) {
        const match = line.match(/Assertion \[([^\]]+)\] has status\s+([A-Z]+)/i);
        if (match) {
          const assertion = {
            name: match[1],
            status: normalizeAssertStatus(match[2]),
            time: extractTime(line)
          };
          if (currentStep) {
            currentStep.assertions.push(assertion);
            // Si l'assertion échoue, capturer le code HTTP du step si disponible
            if (assertion.status === 'FAIL' && currentStep.statusCode) {
              assertion.httpStatusCode = currentStep.statusCode;
            }
          }
        }
      }

      // Assertion failure details
      else if (line.includes('ASSERTION FAILED')) {
        const errorMatch = line.match(/ASSERTION FAILED -> (.+)/);
        if (errorMatch && currentStep) {
          const errorMsg = errorMatch[1];
          // Extraire le code HTTP si présent dans le message d'erreur
          const statusCodeMatch = errorMsg.match(/status code:\s*(\d{3})/i);
          if (statusCodeMatch) {
            currentStep.statusCode = Number(statusCodeMatch[1]);
          }
          
          // Démarrer la collecte du message d'erreur multi-lignes
          errorMessageBuffer = errorMsg;
          collectingErrorMessage = true;
          
          // Ajouter temporairement l'erreur (sera mise à jour avec le message complet)
          const lastAssertion = currentStep.assertions[currentStep.assertions.length - 1];
          if (lastAssertion && lastAssertion.status === 'FAIL') {
            lastAssertion.errorMessage = errorMsg;
            if (statusCodeMatch) {
              lastAssertion.httpStatusCode = Number(statusCodeMatch[1]);
            }
          } else {
            // Pas d'assertion trouvée, ajouter l'erreur directement au step
            if (!currentStep.errorDetails) currentStep.errorDetails = [];
            currentStep.errorDetails.push({
              time: extractTime(line),
              message: errorMsg,
              httpStatusCode: statusCodeMatch ? Number(statusCodeMatch[1]) : undefined
            });
          }
        }
      }

      // TestCase end
      else if (line.includes('Finished running TestCase')) {
        const match = line.match(/Finished running TestCase \[([^\]]+)\].*?time taken:?\s*(\d+)\s*ms.*?status:\s*(PASS|FAIL|FAILED|VALID|INVALID)/i);
        const altMatch = line.match(/Finished running TestCase \[([^\]]+)\].*status:\s*([A-Z]+)/i);
        if (match || altMatch) {
          const testCaseName = (match || altMatch)[1];
          const statusRaw = (match || altMatch)[(match ? 3 : 2)];
          const durationMs = match ? Number(match[2]) : undefined;
          const status = normalizeAssertStatus(statusRaw);

          // Finaliser step courant au moment de fin du test case si ouvert
          const endT = extractTime(line);
          if (currentStep && currentStep.startTime) {
            currentStep.endTime = endT;
            const d = toMs(endT) - toMs(currentStep.startTime);
            if (!isNaN(d)) currentStep.durationMs = Math.max(0, d);
          }

          const finishedTestCase = testCaseStack.find(tc => tc.name === testCaseName);
          if (finishedTestCase) {
            // Mettre à jour les statuts des steps d'abord
            updateStepStatuses(finishedTestCase);
            
            // Ensuite appliquer le statut du log (priorise FAIL)
            if (status === 'FAIL' || finishedTestCase.status === 'FAIL') {
              finishedTestCase.status = 'FAIL';
            } else {
              finishedTestCase.status = status;
            }
            finishedTestCase.endTime = endT;
            if (durationMs !== undefined) finishedTestCase.durationMs = durationMs;

            // Retrait de la stack
            testCaseStack = testCaseStack.filter(tc => tc.name !== testCaseName);

            // Réinitialisation des références
            if (testCaseStack.length === 0) {
              currentTestCase = null;
              nestedTestCase = null;
              currentStep = null;
              nestedStep = null;
            } else {
              currentTestCase = testCaseStack[0];
              nestedTestCase = testCaseStack.length > 1 ? testCaseStack[testCaseStack.length - 1] : null;
            }
          }
        }
      }      // Test Suite end (optionnel)
      else if (line.includes('Finished running TestSuite')) {
        const match = line.match(/Finished running TestSuite \[([^\]]+)\].*?time taken:?\s*(\d+)\s*ms.*?(?:status:\s*([A-Z]+))?/i);
        if (match && currentSuite && currentSuite.name === match[1]) {
          currentSuite.endTime = extractTime(line);
          currentSuite.durationMs = Number(match[2]);
          const sRaw = match[3];
          if (sRaw) currentSuite.status = normalizeAssertStatus(sRaw);
          else {
            // Déduire du contenu
            const hasFail = currentSuite.testCases.some(tc => tc.status && tc.status !== 'PASS');
            currentSuite.status = hasFail ? 'FAIL' : 'PASS';
          }
        }
      }
    });
    
    // Propagation finale des statuts pour tous les test suites
    testSuites.forEach(suite => {
      suite.testCases.forEach(tc => {
        updateStepStatuses(tc);
      });
      
      // Re-vérifier le statut de la suite après mise à jour des test cases
      const failedTestCases = suite.testCases.filter(tc => tc.status === 'FAIL');
      
      if (failedTestCases.length > 0) {
        suite.status = 'FAIL';
      }
    });
    
    return testSuites;
  };

  const extractTime = (line) => {
    const match = line.match(/^(\d{2}:\d{2}:\d{2},\d{3})/);
    return match ? match[1] : '';
  };

  const updateStepStatuses = (testCase) => {
    testCase.steps.forEach(step => {
      // Mettre à jour récursivement les test cases imbriqués d'abord
      if (step.nestedTestCases) {
        step.nestedTestCases.forEach(nestedTC => {
          updateStepStatuses(nestedTC);
        });
        
        // Si un test case imbriqué a échoué, le step parent échoue aussi
        const hasFailedNestedTC = step.nestedTestCases.some(ntc => ntc.status === 'FAIL');
        if (hasFailedNestedTC) {
          step.status = 'FAIL';
          return; // Le step est déjà marqué en échec
        }
      }
      
      // Déterminer le statut du step en fonction des assertions et des erreurs
      if (step.assertions.length > 0) {
        step.status = step.assertions.some(a => a.status === 'FAIL') ? 'FAIL' : 'PASS';
      } else if (step.errorDetails && step.errorDetails.length > 0) {
        // Si le step a des erreurs détectées mais pas d'assertions, le marquer en échec
        step.status = 'FAIL';
      } else {
        step.status = 'PASS'; // Assume pass if no assertions and no errors
      }
    });
    
    // Propager le statut d'échec vers le test case parent
    const hasFailedStep = testCase.steps.some(s => s.status === 'FAIL');
    if (hasFailedStep && testCase.status !== 'FAIL') {
      testCase.status = 'FAIL';
    }
  };

  const parsedData = useMemo(() => {
    const result = parseReadyAPILogs(logInput);
    return result;
  }, [logInput]);

  const toggleExpanded = (path) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedItems(newExpanded);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PASS':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'FAIL':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'RUNNING':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <Play className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PASS':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'FAIL':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'RUNNING':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const parseAssertionError = (raw) => {
    if (!raw) return { main: raw, technical: '' };
    const isoMatch = raw.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);
    if (isoMatch) {
      const idx = raw.indexOf(isoMatch[0]);
      return { main: raw.slice(0, idx).trim(), technical: raw.slice(idx).trim() };
    }
    const soapMatch = raw.match(/\d{2}:\d{2}:\d{2},\d{3}\s+ERROR\s+\[SoapUIProTestCaseRunner\]/);
    if (soapMatch) {
      const idx = raw.indexOf(soapMatch[0]);
      return { main: raw.slice(0, idx).trim(), technical: raw.slice(idx).trim() };
    }
    return { main: raw, technical: '' };
  };

  const renderTestCase = (testCase, path, level = 0) => {
    const isExpanded = expandedItems.has(path);
    const hasChildren = testCase.steps && testCase.steps.length > 0;
    
    return (
      <div key={path} className={`${level > 0 ? 'ml-6 mt-2' : 'mt-4'}`}>
        <div
          className={`flex items-center p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${getStatusColor(testCase.status)}`}
          onClick={() => hasChildren && toggleExpanded(path)}
        >
          {hasChildren && (
            <span className="mr-2">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          )}
          {getStatusIcon(testCase.status)}
          <span className="ml-2 font-medium">
            {testCase.isNested ? '↳ ' : ''}{testCase.name}
          </span>
          <span className="ml-auto text-sm text-gray-500 flex items-center gap-3">
            {testCase.startTime && <span>Début: {testCase.startTime}</span>}
            {typeof testCase.durationMs === 'number' && (
              <span>Durée: {Math.round(testCase.durationMs)} ms</span>
            )}
          </span>
        </div>
        
        {isExpanded && hasChildren && (
          <div className="mt-2">
            {testCase.steps.map((step, stepIndex) => renderStep(step, `${path}-step-${stepIndex}`, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderStep = (step, path, level = 0) => {
    const isExpanded = expandedItems.has(path);
    const hasChildren = (step.assertions && step.assertions.length > 0) || (step.nestedTestCases && step.nestedTestCases.length > 0) || (step.errorDetails && step.errorDetails.length > 0);
    const hasHttpError = step.statusCode && (step.statusCode >= 400 || step.status === 'FAIL');
    
    return (
      <div key={path} className={`ml-${level * 4} mt-2`}>
        <div
          className={`flex items-center p-2 rounded border cursor-pointer hover:bg-gray-50 ${getStatusColor(step.status)}`}
          onClick={() => hasChildren && toggleExpanded(path)}
        >
          {hasChildren && (
            <span className="mr-2">
              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </span>
          )}
          {getStatusIcon(step.status)}
          <span className="ml-2 flex-1">{step.name}</span>
          <span className="ml-auto text-xs text-gray-500 flex items-center gap-3">
            {step.startTime && <span>Début: {step.startTime}</span>}
            {typeof step.durationMs === 'number' && <span>Durée: {Math.round(step.durationMs)} ms</span>}
            {typeof step.responseTimeMs === 'number' && <span>Resp: {Math.round(step.responseTimeMs)} ms</span>}
            {typeof step.statusCode === 'number' && (
              <span className={hasHttpError ? 'font-bold text-red-600' : ''}>
                HTTP: {step.statusCode}
              </span>
            )}
          </span>
        </div>
        
        {isExpanded && hasChildren && (
          <div className="mt-1">
            {/* Nested Test Cases */}
            {step.nestedTestCases && step.nestedTestCases.map((nestedTC, index) => 
              renderTestCase(nestedTC, `${path}-nested-${index}`, level + 1)
            )}
            
            {/* Step Error Details (critiques, affichés en premier) */}
            {step.errorDetails && step.errorDetails.length > 0 && (
              <div className="ml-8 mt-1 p-3 rounded border-2 border-red-300 bg-red-50 text-sm">
                <strong className="text-red-800 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Erreurs détectées:
                </strong>
                {step.errorDetails.map((err, errIndex) => (
                  <div key={errIndex} className="mt-2 text-red-700">
                    <div className="flex items-start gap-2">
                      <span className="text-gray-500 text-xs">{err.time}</span>
                      <div className="flex-1">
                        <div className="font-medium">{err.message}</div>
                        {err.httpStatusCode && (
                          <div className="mt-1 text-xs">
                            <span className="px-2 py-1 bg-red-200 rounded font-bold">
                              HTTP {err.httpStatusCode}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Assertions */}
            {step.assertions && step.assertions.map((assertion, assertionIndex) => (
              <div key={`${path}-assertion-${assertionIndex}`} className={`ml-8 mt-1 p-2 rounded border text-sm ${getStatusColor(assertion.status)}`}>
                <div className="flex items-center">
                  {getStatusIcon(assertion.status)}
                  <span className="ml-2 font-medium">{assertion.name}</span>
                  <span className="ml-auto flex items-center gap-2">
                    {assertion.httpStatusCode && (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${assertion.status === 'FAIL' ? 'bg-red-200 text-red-800' : 'bg-green-100 text-green-700'}`}>
                        HTTP {assertion.httpStatusCode}
                      </span>
                    )}
                    {assertion.time && (
                      <span className="text-xs text-gray-500">{assertion.time}</span>
                    )}
                  </span>
                </div>
                {assertion.errorMessage && (() => {
                  const { main, technical } = parseAssertionError(assertion.errorMessage);
                  return (
                    <div className="mt-2 text-xs">
                      <div className="text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 font-medium">
                        {main}
                      </div>
                      {technical && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-gray-400 hover:text-gray-600 select-none">Détails techniques</summary>
                          <pre className="mt-1 text-gray-500 bg-gray-50 border border-gray-100 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">{technical}</pre>
                        </details>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const [testCases, setTestCases] = useState([]);
  const [relatedBugsMap, setRelatedBugsMap] = useState({}); // map: testName -> [bugs]
  const [showBugsModal, setShowBugsModal] = useState(false);
  const [modalBugs, setModalBugs] = useState([]);
  const [modalBugTestName, setModalBugTestName] = useState(null);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [selectedAiTestCase, setSelectedAiTestCase] = useState(null);
  
  // Initialize nonReproMap from localStorage
  const [nonReproMap, setNonReproMap] = useState(() => {
    try {
      const storageKey = `readyapi:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`;
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const data = JSON.parse(raw);
        const today = new Date().toDateString();
        if (data.date === today && data.values) {
          return data.values;
        }
      }
    } catch (e) {
      console.warn('Failed to load nonReproMap from localStorage:', e);
    }
    return {};
  });
  
  const [loading, setLoading] = useState(true); // chargement des tests
  const [logsLoading, setLogsLoading] = useState(false); // chargement des logs en arrière-plan
  const [error, setError] = useState(null);
  const [showTestCaseModal, setShowTestCaseModal] = useState(false);
  const [selectedTestCaseName, setSelectedTestCaseName] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedLogsTestCase, setSelectedLogsTestCase] = useState(null);
  const [fullLogContent, setFullLogContent] = useState(null);
  const [fullLogLoading, setFullLogLoading] = useState(false);
  // Trace logs modal state
  const [showTraceModal, setShowTraceModal] = useState(false);
  const [selectedTraceId, setSelectedTraceId] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceResult, setTraceResult] = useState(null);
  const [traceError, setTraceError] = useState(null);
  // Elastic authentication state
  const [showElasticAuthModal, setShowElasticAuthModal] = useState(false);
  const [pendingTraceId, setPendingTraceId] = useState(null);
  const [elasticZone, setElasticZone] = useState('qa1');
  const zoneDuBuild = useZoneDeTrace(buildId, null);

  // Cache mémoire simple pour éviter refetch et flicker
  const testCasesCacheRef = useRef({});
  useEffect(() => {
    let cancelled = false;
    const key = `${version}::run::${runId}`;
    const cached = testCasesCacheRef.current[key];
    if (cached) {
      setTestCases(cached);
      setLoading(false);
      return; // skip réseau
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        const results = await azureDevOpsService.getTestCasesForRun(runId, version);
        if (!cancelled) {
          testCasesCacheRef.current[key] = results;
          setTestCases(results);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('❌ Error in getTestCasesForRun:', err);
          setError('Erreur lors de la récupération des tests.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [runId, version]);

  // Charger les bugs liés pour chaque test case (lazy batched fetch)
  useEffect(() => {
    let cancelled = false;
    if (!testCases || testCases.length === 0) {
      setRelatedBugsMap({});
      return;
    }

    const fetchRelatedBugs = async () => {
      const map = {};
      const concurrency = 5;
      for (let i = 0; i < testCases.length && !cancelled; i += concurrency) {
        const batch = testCases.slice(i, i + concurrency);
        try {
          const promises = batch.map(tc =>
            azureDevOpsService.getRelatedBugsForTestCaseByTitle(tc.name).catch(err => {
              console.warn('Erreur getRelatedBugs for', tc.name, err);
              return [];
            })
          );
          const results = await Promise.all(promises);
          batch.forEach((tc, idx) => {
            map[tc.name] = results[idx] || [];
          });
        } catch (err) {
          console.error('Erreur batch fetching related bugs:', err);
        }
      }

      try {
        const localMap = await bugLinkService.getForBuild(pipelineId, buildId);
        Object.entries(localMap).forEach(([testName, bugs]) => {
          const existing = map[testName] || [];
          const merged = [...existing];
          bugs.forEach(b => { if (!merged.some(m => m.id === b.id)) merged.push({ ...b, local: true }); });
          map[testName] = merged;
        });
      } catch (err) {
        console.warn('Erreur récupération bugs liés locaux:', err);
      }

      if (!cancelled) setRelatedBugsMap(map);
    };

    fetchRelatedBugs();
    return () => { cancelled = true; };
  }, [testCases, pipelineId, buildId]);

  // Persist nonReproMap to localStorage
  useEffect(() => {
    try {
      const storageKey = `readyapi:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`;
      const today = new Date().toDateString();
      const data = { date: today, values: nonReproMap };
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save nonReproMap to localStorage:', e);
    }
  }, [nonReproMap, pipelineId, runId, testSuiteName]);

  const [nonReproLoading, setNonReproLoading] = useState(new Set());

  const markNonRepro = async (testName, testResultId) => {
    if (nonReproMap[testName] || nonReproLoading.has(testName)) return;
    setNonReproLoading(prev => new Set([...prev, testName]));
    try {
      await azureDevOpsService.updateTestResultOutcome(runId, testResultId);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } catch (err) {
      console.error('Erreur mise à jour AzDO non-reproduit:', err);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } finally {
      setNonReproLoading(prev => { const n = new Set(prev); n.delete(testName); return n; });
    }
  };

  const [rootCauseMap, setRootCauseMap] = useState({});
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [notesMap, setNotesMap] = useState({});
  useEffect(() => {
    if (!buildId) return;
    rootCauseService.getForBuild(pipelineId, buildId)
      .then(({ rootCauses, notes }) => { setRootCauseMap(rootCauses); setNotesMap(notes); })
      .catch(err => console.warn('Failed to load root causes:', err));
  }, [pipelineId, buildId]);
  const handleRootCause = async (testName, cause) => {
    const prev = rootCauseMap[testName];
    setRootCauseMap(m => ({ ...m, [testName]: cause }));
    try {
      if (cause) {
        await rootCauseService.set({ pipelineId, buildId, runId, testName, rootCause: cause, squad: testSuiteName });
      } else {
        await rootCauseService.remove({ pipelineId, buildId, testName });
      }
    } catch (err) {
      console.error('Failed to save root cause:', err);
      setRootCauseMap(m => ({ ...m, [testName]: prev }));
    }
  };
  const handleBatchRootCause = async (cause) => {
    const names = [...selectedTests];
    const prev = { ...rootCauseMap };
    setRootCauseMap(m => {
      const next = { ...m };
      if (cause) names.forEach(nm => { next[nm] = cause; });
      else names.forEach(nm => { delete next[nm]; });
      return next;
    });
    try {
      await Promise.all(names.map(nm =>
        cause
          ? rootCauseService.set({ pipelineId, buildId, runId, testName: nm, rootCause: cause, squad: testSuiteName })
          : rootCauseService.remove({ pipelineId, buildId, testName: nm })
      ));
    } catch (err) {
      console.error('Failed to save batch root cause:', err);
      setRootCauseMap(prev);
    }
    setSelectedTests(new Set());
  };

  const handleNoteSave = async (testName, notes) => {
    try {
      await rootCauseService.setNotes({ pipelineId, buildId, testName, notes });
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  const [linkBugStatus, setLinkBugStatus] = useState(null);

  const linkOneBug = async (testName, bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      throw new Error(`#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})`);
    }
    let linked = summary;
    try {
      await azureDevOpsService.linkBugToTestCaseByTitle(testName, summary.id);
    } catch (err) {
      if (!/Test Case introuvable/.test(err.message || '')) throw err;
      // Pas de Test Case ADO pour ce test: on garde le lien côté app uniquement
      await bugLinkService.add({ pipelineId, buildId, testName, bug: summary });
      linked = { ...summary, local: true };
    }
    setRelatedBugsMap(prev => {
      const existing = prev[testName] || [];
      if (existing.some(b => b.id === linked.id)) return prev;
      return { ...prev, [testName]: [...existing, linked] };
    });
  };

  const handleBatchLinkBug = async (bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      setLinkBugStatus({ type: 'error', message: `#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})` });
      return;
    }

    const names = [...selectedTests].filter(
      nm => !(relatedBugsMap[nm] || []).some(b => b.id === summary.id)
    );

    if (names.length === 0) {
      setLinkBugStatus({ type: 'error', message: 'Tests déjà liés à ce bug' });
      return;
    }

    const linkedByTest = {};
    const failed = [];
    let localCount = 0;

    await Promise.all(names.map(async (nm) => {
      try {
        await azureDevOpsService.linkBugToTestCaseByTitle(nm, summary.id);
        linkedByTest[nm] = summary;
      } catch (err) {
        if (!/Test Case introuvable/.test(err.message || '')) {
          failed.push(nm);
          return;
        }
        try {
          await bugLinkService.add({ pipelineId, buildId, testName: nm, bug: summary });
          linkedByTest[nm] = { ...summary, local: true };
          localCount++;
        } catch {
          failed.push(nm);
        }
      }
    }));

    setRelatedBugsMap(prev => {
      const next = { ...prev };
      Object.entries(linkedByTest).forEach(([nm, bug]) => {
        const existing = next[nm] || [];
        next[nm] = existing.some(b => b.id === bug.id) ? existing : [...existing, bug];
      });
      return next;
    });

    const successCount = Object.keys(linkedByTest).length;
    setLinkBugStatus(
      failed.length > 0
        ? { type: 'error', message: `${successCount} test(s) lié(s), ${failed.length} échec(s)` }
        : { type: 'success', message: `${successCount} test(s) lié(s) au bug #${summary.id}${localCount ? ` (${localCount} en local, hors ADO)` : ''}` }
    );
    setSelectedTests(new Set());
  };

  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  // Initialize state from URL (URL params take priority over initialSearchTerm)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const q = sp.get('q');
    const status = sp.get('status');
    const sort = sp.get('sort');
    if (q) setSearchTerm(q);
    else if (initialSearchTerm) setSearchTerm(initialSearchTerm);
    if (status && ['all','passed','failed'].includes(status)) setStatusFilter(status);
    if (sort && ['name','status','duration'].includes(sort)) setSortBy(sort);
  }, [pipelineId, buildId, runId, testSuiteName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to push current view state to URL
  const syncUrl = (overrides = {}) => {
    // Use replaceState to avoid full navigation flicker
    const sp = new URLSearchParams(window.location.search);
    const nextQ = overrides.q ?? searchTerm;
    const nextStatus = overrides.status ?? statusFilter;
    const nextSort = overrides.sort ?? sortBy;
    const changed = (
      sp.get('q') !== nextQ ||
      sp.get('status') !== nextStatus ||
      sp.get('sort') !== nextSort ||
      sp.get('version') !== version
    );
    if (!changed) return;
    sp.set('version', version);
    sp.set('q', nextQ);
    sp.set('status', nextStatus);
    sp.set('sort', nextSort);
    const basePath = `/readyapi/pipeline/${pipelineId}/build/${buildId}/run/${runId}/testsuite/${encodeURIComponent(testSuiteName)}`;
    window.history.replaceState(null, '', `${basePath}?${sp.toString()}`);
  };

  // Reflect filters into URL on change
  useEffect(() => { syncUrl(); }, [searchTerm, statusFilter, sortBy]);

  const filteredTests = testCases
    .filter(test => {
      const matchesSearch = test.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = matchesStatusFilter(test.status, statusFilter);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'duration':
          return b.duration - a.duration;
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return a.name.localeCompare(b.name);
      }
    });

  // Statistiques pour la vue résumé
  const { total: totalTests, passed: passedTests, failed: failedTests, skipped: skippedTests, successRate } = computeTestStats(testCases);

  // Afficher le chargement si les logs sont en cours de chargement
  if (loading || logsLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3">
        <div className="animate-spin h-8 w-8 rounded-full border-4 border-blue-200 border-t-blue-600"></div>
        <div className="text-sm text-gray-600">
          Chargement des données tests & logs...
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-600 text-center">{error}</div>;
  }  


  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Suite de Tests {runId}</h2>
            <p className="text-gray-600">{buildId}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const target = `/pipeline/${pipelineId}/build/${buildId}?version=${version}&nav=build${window.location.search.includes('debug=1') ? '&debug=1' : ''}`;
                navigate(target);
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
            >
              ← Retour Build
            </button>
          </div>
        </div>

        {/* Statistiques de la test suite */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Total Tests</span>
              <span className="text-lg font-bold text-gray-900">{totalTests}</span>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Passés</span>
              <span className="text-lg font-bold text-green-600">{passedTests}</span>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Échoués</span>
              <span className="text-lg font-bold text-red-600">{failedTests}</span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Ignorés</span>
              <span className="text-lg font-bold text-gray-500">{skippedTests}</span>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Taux Réussite</span>
              <span className="text-lg font-bold text-blue-600">{successRate}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Tests list */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="space-y-6">
            {/* Screenshot / Copy buttons (top-right fixed for Test Suite Details) */}
            <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
              <button
                onClick={async () => {
                  try {
                    toast.loading('Capture en cours...', { id: 'screenshot-ts' });
                    
                    // Scroll to top before capture
                    window.scrollTo(0, 0);
                    
                    // Wait for fonts and styles to load
                    await document.fonts.ready;
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    const el = document.body;
                    const canvas = await html2canvas(el, { 
                      scale: 2,
                      useCORS: true,
                      allowTaint: true,
                      backgroundColor: '#ffffff',
                      windowWidth: document.documentElement.scrollWidth,
                      windowHeight: document.documentElement.scrollHeight,
                      x: 0,
                      y: 0,
                      scrollY: 0,
                      scrollX: 0,
                      logging: false,
                      foreignObjectRendering: false,
                      removeContainer: true
                    });
                    const dataUrl = canvas.toDataURL('image/png');
                    const link = document.createElement('a');
                    link.href = dataUrl;
                    link.download = `test-suite-${testSuiteName}-${Date.now()}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success('Screenshot téléchargé', { id: 'screenshot-ts' });
                  } catch (err) {
                    console.error(err);
                    toast.error('Erreur capture', { id: 'screenshot-ts' });
                  }
                }}
                className="px-2 py-2 rounded flex items-center justify-center text-blue-600 hover:text-blue-800"
                title="Capture d'écran"
                aria-label="Capture d'écran TestSuite"
              >
                <Camera className="w-5 h-5" />
              </button>
            </div>
              {/* Filters */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Rechercher un test..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="passed">Passés</option>
                    <option value="failed">Échoués</option>
                    <option value="skipped">Ignorés</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="name">Trier par nom</option>
                    <option value="status">Trier par statut</option>
                  </select>
                </div>
              </div>

              {/* Test Cases Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <Toaster position="top-right" />
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Tests Filtrés ({filteredTests.length})
                  </h3>
                </div>

                <TestResultsTable
                  tests={filteredTests}
                  columns={{ rootCause: true, relance: true }}
                  nameLabel="Nom du Test"
                  StatusBadge={StatusBadge}
                  selectedTests={selectedTests}
                  onSelectedTestsChange={setSelectedTests}
                  relatedBugsMap={relatedBugsMap}
                  nonReproMap={nonReproMap}
                  nonReproLoading={nonReproLoading}
                  bugsMode="exclusive"
                  onMarkNonRepro={markNonRepro}
                  onLinkBug={linkOneBug}
                  onShowBugs={(bugs, testName) => { setModalBugs(bugs); setModalBugTestName(testName); setShowBugsModal(true); }}
                  rootCauseMap={rootCauseMap}
                  notesMap={notesMap}
                  onRootCause={handleRootCause}
                  onNoteChange={(testName, val) => setNotesMap(m => ({ ...m, [testName]: val }))}
                  onNoteBlur={handleNoteSave}
                  getRelanceStatus={getRelanceStatus}
                  renderName={test => (
                    <div className="flex items-center gap-2">
                      {test.isNewFailure && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300">
                          New
                        </span>
                      )}
                      <button
                        onClick={() => {
                          setSelectedTestCaseName(test.name);
                          setShowTestCaseModal(true);
                        }}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                      >
                        {test.name}
                      </button>
                    </div>
                  )}
                  renderActions={test => (
                    <>
                      {test.logUrl && (
                        <button
                          onClick={async () => {
                            setSelectedLogsTestCase(test);
                            setShowLogsModal(true);
                            setFullLogContent(null);
                            setFullLogLoading(true);
                            try {
                              const logContent = await azureDevOpsService.getFullTestCaseLog(test);
                              setFullLogContent(logContent);
                            } catch (err) {
                              console.error('Erreur récupération log complet:', err);
                            } finally {
                              setFullLogLoading(false);
                            }
                          }}
                          className="text-blue-600 hover:text-blue-800"
                          title={`Afficher les logs de ${test.name}`}
                        >
                          Logs
                        </button>
                      )}
                      {version === 'develop' && (
                        <button
                          onClick={() => handleRelancer(test.name)}
                          className="text-blue-600 hover:text-blue-800"
                          disabled={runningTests[test.name]?.status === 'inProgress'}
                        >
                          {runningTests[test.name]?.status === 'inProgress' ? 'Relance...' : 'Relancer'}
                        </button>
                      )}
                      {test.status === 'failed' && (
                        <button
                          onClick={() => {
                            const scenario = logInput ? extractTestCaseLog(logInput, test.name) : null;
                            setSelectedAiTestCase({ ...test, scenario });
                            setShowAiAnalysis(true);
                          }}
                          className="text-purple-600 hover:text-purple-800"
                          title={`Analyser ${test.name} avec l'IA`}
                        >
                          Analyser
                        </button>
                      )}
                      <button
                        onClick={() => {
                          const currentPathLocal = window.location.pathname.replace(/^\/readyapi/, '');
                          const currentSearchLocal = window.location.search || '';
                          const rawReturn = `${currentPathLocal}${currentSearchLocal}`;
                          const returnUrl = encodeURIComponent(rawReturn);
                          navigate(`/testcase/${encodeURIComponent(test.name)}/history/${pipelineId}?version=${version}&backPipelineId=${pipelineId}&backBuildId=${buildId}&backRunId=${runId}&backTestSuiteName=${encodeURIComponent(testSuiteName)}&returnUrl=${returnUrl}&debug=${window.location.search.includes('debug=1') ? '1' : '0'}`);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Historique
                      </button>
                    </>
                  )}
                  emptyState={(
                    <div className="text-center py-12">
                      <TestTube className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun test trouvé</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Essayez de modifier vos filtres de recherche.
                      </p>
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      

      {/* Modal pour afficher les détails d'un test case */}
      {showTestCaseModal && selectedTestCaseName && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowTestCaseModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Détails: {selectedTestCaseName}
              </h3>
              <button
                onClick={() => setShowTestCaseModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {(() => {
                // Extraire les logs du test case spécifique
                const testCaseLog = extractTestCaseLog(logInput, selectedTestCaseName);
                
                // Créer un log avec une test suite wrapper pour que le parser fonctionne
                const wrappedLog = `[SoapUIProTestCaseRunner] Running Test Suite [${testSuiteName}]\n${testCaseLog}\n[SoapUIProTestCaseRunner] Finished running Test Suite [${testSuiteName}]`;
                
                // Parser ces logs wrappés
                const parsedData = parseReadyAPILogs(wrappedLog);
                
                if (parsedData.length > 0 && parsedData[0].testCases.length > 0) {
                  const testCase = parsedData[0].testCases[0];
                  
                  return (
                    <div className="space-y-4">
                      {/* Informations du test case */}
                      <div className={`p-4 rounded-lg border ${getStatusColor(testCase.status)}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(testCase.status)}
                            <span className="font-semibold text-lg">{testCase.name}</span>
                          </div>
                          <div className="text-sm text-gray-600 flex gap-4">
                            {testCase.startTime && <span>Début: {testCase.startTime}</span>}
                            {typeof testCase.durationMs === 'number' && (
                              <span>Durée: {Math.round(testCase.durationMs)} ms</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Steps du test case - affichés directement */}
                      <div className="space-y-2">
                        {testCase.steps && testCase.steps.length > 0 ? (
                          testCase.steps.map((step, stepIndex) => 
                            renderStep(step, `modal-step-${stepIndex}`, 0)
                          )
                        ) : (
                          <div className="text-center py-4 text-gray-500">
                            Aucun step trouvé pour ce test case.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div className="text-center py-8 text-gray-500">
                    <p>Aucun détail trouvé pour ce test case.</p>
                    <pre className="mt-4 text-xs text-left bg-gray-100 p-4 rounded overflow-auto max-h-60">
                      {testCaseLog}
                    </pre>
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowTestCaseModal(false)}
                className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal agrégé des logs ReadyAPI */}
      {showLogsModal && selectedLogsTestCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={() => { setShowLogsModal(false); setSelectedLogsTestCase(null); setFullLogContent(null); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Logs: {selectedLogsTestCase?.name}</h3>
              <button onClick={() => { setShowLogsModal(false); setSelectedLogsTestCase(null); setFullLogContent(null); }} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              {(() => {
                const tc = selectedLogsTestCase;
                
                // Helper to render clickable TraceIds
                const TRACE_REGEX = /(X-Trace-Id:\s*\[([0-9a-fA-F\-]{16,64})\])/g;
                const renderWithTraceLinks = (text) => {
                  if (!text) return null;
                  const parts = [];
                  let lastIndex = 0;
                  let match;
                  while ((match = TRACE_REGEX.exec(text)) !== null) {
                    const full = match[1];
                    const id = match[2];
                    const start = match.index;
                    if (start > lastIndex) {
                      parts.push(text.slice(lastIndex, start));
                    }
                    parts.push(
                      <button
                        key={`trace-${start}`}
                        className="underline text-blue-600 hover:text-blue-800"
                        onClick={() => openTraceModal(id)}
                        title={`Chercher TraceId ${id}`}
                      >
                        {full}
                      </button>
                    );
                    lastIndex = start + full.length;
                  }
                  if (lastIndex < text.length) {
                    parts.push(text.slice(lastIndex));
                  }
                  return parts;
                };
                
                return (
                  <div className="border rounded-lg p-4 shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(tc.status)}
                        <span className="font-semibold">{tc.name}</span>
                        {tc.isNewFailure && (
                          <span className="ml-2 text-xs font-medium px-2 py-1 bg-red-100 text-red-700 rounded">Nouveau</span>
                        )}
                      </div>
                      {tc.logUrl && (
                        <a
                          href={tc.logUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 text-sm hover:underline flex items-center gap-1"
                        >
                          Voir sur Azure DevOps <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    {tc.error && (
                      <div className="mb-2 text-sm text-red-600 whitespace-pre-wrap">
                        {tc.error}
                      </div>
                    )}
                    
                    {fullLogLoading && (
                      <div className="flex items-center gap-2 text-blue-600 py-4">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Chargement du log complet depuis les attachements...</span>
                      </div>
                    )}
                    
                    {!fullLogLoading && fullLogContent && (
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Log complet récupéré depuis l'attachement
                        </div>
                        <pre className="text-xs whitespace-pre-wrap font-mono overflow-auto max-h-[500px] bg-white p-3 rounded border">
                          {renderWithTraceLinks(fullLogContent)}
                        </pre>
                      </div>
                    )}
                    
                    {!fullLogLoading && !fullLogContent && tc.stackTrace && (
                      <>
                        <div className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          Log partiel (stackTrace limité en caractères)
                        </div>
                        <pre className="text-xs whitespace-pre-wrap font-mono overflow-auto max-h-[500px] bg-white p-3 rounded border">
                          {renderWithTraceLinks(DOMPurify.sanitize(String(tc.stackTrace)))}
                        </pre>
                      </>
                    )}
                    
                    {!fullLogLoading && !fullLogContent && !tc.stackTrace && (
                      <div className="text-xs text-gray-500 italic">Aucun log disponible.</div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => { setShowLogsModal(false); setSelectedLogsTestCase(null); setFullLogContent(null); }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm text-gray-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Elastic Authentication Modal */}
      <ElasticAuthModal
        isOpen={showElasticAuthModal}
        onClose={() => { setShowElasticAuthModal(false); setPendingTraceId(null); }}
        onAuthenticate={handleElasticAuth}
        zone={elasticZone}
      />
      {/* Trace Logs Modal */}
      {showTraceModal && selectedTraceId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4" onClick={() => setShowTraceModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-base font-semibold text-gray-900">Logs applicatifs pour TraceId {selectedTraceId}</h3>
              <button onClick={() => setShowTraceModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {traceLoading && (
                <div className="flex items-center gap-2 text-blue-600">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Recherche des logs applicatifs…</span>
                </div>
              )}
              {!traceLoading && traceError && (
                <div className="text-red-600 text-sm">{String(traceError)}</div>
              )}
              {!traceLoading && !traceError && (
                <pre className="text-xs whitespace-pre-wrap font-mono overflow-auto max-h-[70vh] bg-gray-50 p-3 rounded border">
                  {typeof traceResult === 'string' ? traceResult : JSON.stringify(traceResult, null, 2)}
                </pre>
              )}
            </div>
            <div className="p-3 border-t flex justify-end">
              <button onClick={() => setShowTraceModal(false)} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm text-gray-700">Fermer</button>
            </div>
          </div>
        </div>
      )}
      {/* Modal pour afficher les bugs liés d'un test case (ouvert depuis la table) */}
      {showBugsModal && modalBugs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowBugsModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Bugs liés: {modalBugTestName}</h3>
                <p className="text-sm text-gray-500">{modalBugs.length} bug(s) trouvés</p>
              </div>
              <button onClick={() => setShowBugsModal(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              {modalBugs.length === 0 ? (
                <div className="text-gray-600 text-center py-8">Aucun bug lié trouvé.</div>
              ) : (
                modalBugs.map(b => (
                  <div key={b.id} className="border rounded p-3 flex justify-between items-start hover:bg-gray-50 transition-colors">
                    <div className="flex-1">
                      <a href={b.url} target="_blank" rel="noreferrer" className="text-gray-700 font-medium hover:underline">#{b.id} — {b.title}</a>
                      <div className="text-sm text-gray-600 mt-1">Etat: {b.state}</div>
                    </div>
                    <div className="text-right text-sm ml-4">
                      <a href={b.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">Ouvrir <ExternalLink className="w-4 h-4"/></a>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-end p-4 border-t bg-gray-50">
              <button onClick={() => setShowBugsModal(false)} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">Fermer</button>
            </div>
          </div>
        </div>
      )}
      <BatchRootCauseBar
        count={selectedTests.size}
        onApply={handleBatchRootCause}
        onClear={() => { setSelectedTests(new Set()); setLinkBugStatus(null); }}
        onLinkBug={handleBatchLinkBug}
        linkBugStatus={linkBugStatus}
      />
      <AiAnalysisDrawer
        open={showAiAnalysis}
        onClose={() => {
          setShowAiAnalysis(false);
          setSelectedAiTestCase(null);
        }}
        testCase={selectedAiTestCase}
        context={{
          source: 'readyapi',
          pipelineId,
          buildId,
          runId,
          testSuiteName
        }}
      />
    </div>
  );
};

// Test Case History Component
const TestCaseHistory = ({ pipelineId, testcaseName, navigate, version = 'develop', backPipelineId, backBuildId, backRunId, backTestSuiteName, returnUrl }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await azureDevOpsService.getAutomatedTestHistory(testcaseName, pipelineId);
        console.log('Historique du test:', data);
        console.log('Premier élément:', data[0]);
        
        // Correction des dates manquantes et préparation du tri par timestamp
        const correctedData = data.map((item, index) => {
          // Conserver le timestamp complet pour le tri (avec heure)
          const fullTimestamp = item.dateTimestamp || item.completedDate || item.startedDate || Date.now() - (index * 24 * 60 * 60 * 1000);
          const dateObj = new Date(fullTimestamp);
          
          return {
            ...item,
            dateTimestamp: fullTimestamp,
            date: item.date || dateObj.toISOString().split('T')[0],
            dateTime: dateObj.toISOString(), // Date complète avec heure pour le tri
            value: 1,
            statusDisplay: statusLabel(item.status)
          };
        });
        
        // Tri par timestamp complet (date + heure) au lieu de juste la date
        const sortedData = correctedData.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
        setHistoryData(sortedData);
      } catch (err) {
        console.error('Erreur lors du chargement de l\'historique:', err);
        setError('Impossible de charger l\'historique du test');
      } finally {
        setLoading(false);
      }
    };

    if (testcaseName && pipelineId) {
      fetchHistory();
    }
  }, [testcaseName, pipelineId]);



  const getStatusColor = (status) => {
    switch (status) {
      case 'passed': return '#10b981';
      case 'failed': return '#ef4444';
      case 'inconclusive': return '#f59e0b';
      default: return '#6b7280';
    }
  };
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900">{`Build: ${data.buildId}`}</p>
          <p className="text-gray-600">{`Date: ${data.date}`}</p>
          <p className="text-gray-600">
            Statut: <span className={`font-medium ${
              data.status === 'passed' ? 'text-green-600' : 
              data.status === 'failed' ? 'text-red-600' : 'text-gray-500'
            }`}>
              {statusLabel(data.status)}
            </span>
          </p>
          <p className="text-gray-600">{`Durée: ${data.durationSeconds}s`}</p>
        </div>
      );
    }
    return null;
  };

  const filteredData = historyData.filter(d => {
    const matchStatus = matchesStatusFilter(d.status, statusFilter);
    const matchDate = !dateFilter || d.date === dateFilter;
    return matchStatus && matchDate;
  });

  const { total: totalTests, failed: failedTests, skipped: skippedTests, successRate } = computeTestStats(filteredData);
  const avgDuration = totalTests > 0 ? Math.round(filteredData.reduce((sum, d) => sum + (d.durationSeconds || d.duration || 0), 0) / totalTests) : 0;

  if (loading) {
    return <div className="text-center py-8">Chargement de l'historique...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }

  if (historyData.length === 0) {
    return <div className="text-center py-8 text-gray-500">Aucun historique trouvé pour ce test</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Historique du Test</h2>
            <p className="text-gray-600">{testcaseName}</p>
          </div>
          <button 
            onClick={() => {
              if (returnUrl) {
                let decodedAttempt = returnUrl;
                const steps = [];
                for (let i = 0; i < 5; i++) {
                  try {
                    const next = decodeURIComponent(decodedAttempt);
                    steps.push(next);
                    if (next === decodedAttempt) break;
                    decodedAttempt = next;
                  } catch { steps.push('ERR_DECODE'); break; }
                }
                const finalDecoded = decodedAttempt;
                const pathForNavigate = finalDecoded.replace(/^\/readyapi/, '');
                navigate(pathForNavigate + (pathForNavigate.includes('?') ? '&debug=1' : '?debug=1'));
                return;
              }
              if (backPipelineId && backBuildId && backRunId && backTestSuiteName) {
                let rawName = backTestSuiteName;
                try { rawName = decodeURIComponent(backTestSuiteName); } catch {}
                const encodedName = encodeURIComponent(rawName);
                const fallback = `/pipeline/${backPipelineId}/build/${backBuildId}/run/${backRunId}/testsuite/${encodedName}?version=${version}&tab=tests&status=all&sort=name&q=&sort=name&debug=1`;
                navigate(fallback);
              } else {
                const storedReturnTo = getStoredReturnTo();
                if (storedReturnTo) {
                  window.location.href = storedReturnTo;
                } else {
                  navigate(`/?version=${version}&debug=1`);
                }
              }
            }} 
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors">
            ← Retour
          </button>
        </div>

        {/* Filtres */}
        <div className="mt-4 flex flex-wrap gap-4">
          <select
            className="border px-2 py-1 rounded"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="passed">Passé</option>
            <option value="failed">Échoué</option>
            <option value="skipped">Ignoré</option>
          </select>

          <input
            type="date"
            className="border px-2 py-1 rounded"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />

          <button
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            onClick={() => { setStatusFilter('all'); setDateFilter(''); }}
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Statistiques en en-tête */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{totalTests}</div>
          <div className="text-sm text-gray-600">Exécutions</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{successRate}%</div>
          <div className="text-sm text-gray-600">Taux de réussite</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{failedTests}</div>
          <div className="text-sm text-gray-600">Échecs</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-500">{skippedTests}</div>
          <div className="text-sm text-gray-600">Ignorés</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{avgDuration}ms</div>
          <div className="text-sm text-gray-600">Durée moyenne</div>
        </div>
      </div>

      {/* Graphique de statut uniquement */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibent text-gray-900 mb-4">
          Historique des Statuts d'Exécution
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              domain={[0, 1]}
              tick={false}
              label={{ value: 'Statut', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              formatter={(value, name, props) => [
                props.payload.statusDisplay,
                'Statut'
              ]}
              labelFormatter={(label) => `Date: ${label}`}
            />
            
            {/* Barres colorées selon le statut (vert ou rouge uniquement) */}
            <Bar dataKey="value" name="Statut d'exécution">
              {filteredData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={statusChartColor(entry.status)} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Évolution de la Durée */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution de la Durée</h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={filteredData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              label={{ value: 'Durée (s)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip 
              formatter={(value) => [`${value}s`, 'Durée']}
              labelFormatter={(label) => `Date: ${label}`}
            />
            <Area
              type="monotone"
              dataKey="durationSeconds"
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#colorDuration)"
              name="Durée d'exécution"
            />
            <defs>
              <linearGradient id="colorDuration" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* History Details Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Détails par Build</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Build</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((entry, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.dateTime).toLocaleString('fr-FR', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {entry.buildId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={entry.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {entry.duration}s
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <a
                      href= {entry.logUrl}
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Voir Log
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Main App Component
const ReadyApiPage = () => {
  const { currentPath, params, navigate, debugMode } = useRouter();
  const [pendingFilter, setPendingFilter] = useState('');

  const currentSearch = currentPath.split('?')[1] || '';
  const queryParams = new URLSearchParams(currentSearch);
  const versionParam = queryParams.get('version') || params.version || 'develop';
  const configParam = queryParams.get('config') || params.config || 'default';

  useEffect(() => {
    if (params && params.returnTo) {
      try {
        localStorage.setItem('readyapiReturnTo', params.returnTo);
      } catch {}
    }
  }, [params]);

  const renderContent = () => {
    // Clean path by removing query parameters for route matching
    const cleanPath = currentPath.split('?')[0];
    // Derive route from path to avoid relying on async params updates
    const segs = cleanPath.split('/').filter(Boolean);
    const local = {};
    if (segs[0] === 'pipeline' && segs[1]) {
      local.pipelineId = segs[1];
      if (segs[2] === 'build' && segs[3]) {
        local.buildId = segs[3];
        if (segs[4] === 'run' && segs[5]) {
          local.runId = segs[5];
          if (segs[6] === 'testsuite' && segs[7]) {
            local.testSuiteName = decodeURIComponent(segs.slice(7).join('/'));
          }
        }
      }
    } else if (segs[0] === 'testcase' && segs[1]) {
      // handled below with params for query values
    }
    
    if (cleanPath === '/' || cleanPath === '') {
      return <Dashboard navigate={navigate} configMode={configParam} />;
    } else if (cleanPath.startsWith('/pipeline/') && local.pipelineId) {
      if (local.runId && local.testSuiteName) {
        return (
          <TestSuiteDetails
            pipelineId={local.pipelineId}
            buildId={local.buildId}
            runId={local.runId}
            testSuiteName={local.testSuiteName}
            navigate={navigate}
            version={versionParam}
            initialSearchTerm={pendingFilter}
          />
        );
      } else if (local.buildId) {
        return (
          <BuildDetails 
            pipelineId={local.pipelineId}
            buildId={local.buildId}
            navigate={navigate}
            version={versionParam}
            configMode={configParam}
          />
        );
      } else {
        return (
          <PipelineDetails 
            pipelineId={local.pipelineId}
            navigate={navigate}
            version={versionParam}
            configMode={configParam}
          />
        );
      }
    } else if (cleanPath.startsWith('/testcase/') && params.testcaseName) {
      return (
        <TestCaseHistory 
          testcaseName={params.testcaseName}
          pipelineId={params.pipelineId}
          navigate={navigate}
          version={versionParam}
          backPipelineId={params.backPipelineId}
          backBuildId={params.backBuildId}
          backRunId={params.backRunId}
          backTestSuiteName={params.backTestSuiteName}
          returnUrl={params.returnUrl}
        />
      );
    }

    
    return <div className="text-red-600">Page non trouvée</div>;
  };

  return (
    <div className="min-h-screen bg-gray-100">

      {/* Navigation */}
      <Navigation currentPath={currentPath} navigate={navigate} version={versionParam} />

      {/* Barre de recherche globale */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <TestCaseSearchBar
            onSelect={async (result) => {
              setPendingFilter(result.title);
              try {
                const location = await azureDevOpsService.findTestCaseLocation(
                  result.id, result.title, ALL_READYAPI_PIPELINE_IDS
                );
                if (location) {
                  const version = READYAPI_PIPELINE_TO_VERSION[location.pipelineId] || versionParam;
                  navigate(
                    `/pipeline/${location.pipelineId}/build/${location.buildId}/run/${location.runId}/testsuite/${encodeURIComponent(location.suiteName)}?version=${version}&q=${encodeURIComponent(result.title)}`
                  );
                  return;
                }
              } catch (e) {
                console.warn('findTestCaseLocation ReadyAPI échec:', e);
              }
              navigate('/');
            }}
            pendingFilter={pendingFilter}
            onClearFilter={() => setPendingFilter('')}
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {renderContent()}
      </main>
    </div>
  );
};

export default ReadyApiPage;